"use client";

import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Billboard, ContactShadows, Environment } from "@react-three/drei";
import React, {
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import * as THREE from "three";
import {
  applyRendererGpuTune,
  applyTextureAnisotropy,
  canvasGlProps,
  dprForQuality,
  probeWebGL,
  shadowMapSize,
  type GraphicsQuality,
} from "./webgl-graphics";
import {
  fortCellsAlong,
  fortCellsAlongCorners,
  FORT_LINE_MAX,
  terrainHeight,
  type FortPoint,
} from "./empire-fort";
import {
  bindTerrainView,
  inChannel,
  palette,
  terrainSites,
  terrainStyle,
  terrainTrees,
  MAP_HALF,
  WATERLINE,
} from "./empire-terrain";
import { BeastMesh, CamelRider, HorseRider, Person, WarElephant, type Gait } from "./empire-figures";
import { avenueTrees, landRoutes, RoadNet } from "./empire-roads";
import {
  GableRoof,
  GabledYard,
  LongBarracks,
  roofTex,
  StoneHall,
  stoneTex,
  StoneKeep,
  StoneTower,
  TimberHouse,
  woodTex,
} from "./empire-architecture";

export type { GraphicsQuality };

export type PublicEntity = {
  id: string;
  kind: string;
  team: string | null;
  x: number;
  z: number;
  hp: number;
  maxHp: number;
  carry?: { type: string; amount: number };
  train?: { unit: string; left: number };
  research?: { tech: string; left: number };
  stock?: number;
  order?: string;
  /** Where the current order is walking. The mesh heads there between polls. */
  ox?: number;
  oz?: number;
  /** Villager task: wood, food, gold, stone, build, or idle. */
  job?: string;
  facing?: number;
  constructing?: boolean;
  /** 0–1 villager job progress (build / gather carry). */
  workProgress?: number;
  chronoTier?: string;
  chronoName?: string;
  gateState?: string;
  cargo?: number;
  aboardShipId?: string;
  stance?: string;
  insideId?: string;
};

export type WaterPayload = {
  cell: number;
  mapHalf: number;
  cells: string[];
} | null;

export type FogPayload = {
  cell: number;
  mapHalf: number;
  explored: string[];
  visible: string[];
} | null;

const TEAM_ACCENT: Record<string, string> = {
  amber: "#2a62d6",
  teal: "#2e9a45",
  rose: "#d32626",
  violet: "#7a35d4",
  lime: "#c48a14",
  sky: "#1aa0c8",
  coral: "#d06a30",
  gold: "#c9a227",
};
const TEAM_BANNER: Record<string, string> = {
  amber: "#2a62d6",
  teal: "#2e9a45",
  rose: "#d32626",
  violet: "#7a35d4",
  lime: "#c48a14",
  sky: "#1aa0c8",
  coral: "#d06a30",
  gold: "#c9a227",
};

const STONE = "#d8cfc0";
const TERRACOTTA = "#b54a2a";
const WOOD = "#6b4423";

function StoneMat({ color = "#d5d0c6" }: { color?: string }) {
  return (
    <meshStandardMaterial map={stoneTex()} color={color} roughness={0.92} metalness={0.02} />
  );
}

function RoofMat({ color = TERRACOTTA }: { color?: string }) {
  return (
    <meshStandardMaterial map={roofTex()} color={color} roughness={0.78} metalness={0.02} />
  );
}

function WoodMat({ color = WOOD }: { color?: string }) {
  return (
    <meshStandardMaterial map={woodTex()} color={color} roughness={0.9} metalness={0.02} />
  );
}

function makeTileTexture(
  kind: "grass" | "dirt" | "farm",
  size = 128
): THREE.CanvasTexture {
  const c = document.createElement("canvas");
  c.width = size;
  c.height = size;
  const ctx = c.getContext("2d")!;
  if (kind === "grass") {
    const g = ctx.createLinearGradient(0, 0, size, size);
    g.addColorStop(0, "#5c9a48");
    g.addColorStop(0.5, "#4a8638");
    g.addColorStop(1, "#3d7530");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
    for (let i = 0; i < 680; i++) {
      const x = (i * 17) % size;
      const y = (i * 31) % size;
      ctx.fillStyle =
        i % 3 === 0 ? "#6fb354" : i % 3 === 1 ? "#3d6a2c" : "#7ec45e";
      ctx.fillRect(x, y, 2 + (i % 2), 2 + (i % 3));
    }
    for (let i = 0; i < 60; i++) {
      ctx.fillStyle = "rgba(220,240,140,0.18)";
      ctx.fillRect((i * 41) % size, (i * 23) % size, 3, 1);
    }
  } else if (kind === "dirt") {
    ctx.fillStyle = "#c4a574";
    ctx.fillRect(0, 0, size, size);
    for (let i = 0; i < 280; i++) {
      const x = (i * 13) % size;
      const y = (i * 29) % size;
      ctx.fillStyle = i % 2 === 0 ? "#b89560" : "#d4b888";
      ctx.fillRect(x, y, 3, 2);
    }
  } else {
    ctx.fillStyle = "#5c4020";
    ctx.fillRect(0, 0, size, size);
    for (let row = 0; row < size; row += 4) {
      ctx.fillStyle = row % 8 === 0 ? "#3d7a28" : "#2f5c1e";
      ctx.fillRect(0, row, size, 2);
      ctx.fillStyle = "#6b4423";
      ctx.fillRect(0, row + 2, size, 1);
    }
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

function useEmpireTextures(quality: GraphicsQuality) {
  return useMemo(() => {
    const res = quality === "high" ? 128 : quality === "medium" ? 96 : 64;
    const grass = makeTileTexture("grass", res);
    grass.repeat.set(18, 18);
    const dirt = makeTileTexture("dirt", res);
    dirt.repeat.set(8, 8);
    const farm = makeTileTexture("farm", res);
    farm.repeat.set(2, 3);
    applyTextureAnisotropy(grass, quality);
    applyTextureAnisotropy(dirt, quality);
    applyTextureAnisotropy(farm, quality);
    return { grass, dirt, farm };
  }, [quality]);
}

function Tree({ x, z, scale = 1 }: { x: number; z: number; scale?: number }) {
  const s = scale;
  const boughs: Array<[number, number, number, string]> = [
    [0.95, 1.15, 0.72, "#163c22"],
    [0.78, 1.05, 1.35, "#1d4c2c"],
    [0.6, 0.95, 1.95, "#245836"],
    [0.42, 0.82, 2.5, "#2c6840"],
    [0.24, 0.7, 3.0, "#34784a"],
  ];
  return (
    <group position={[x, 0, z]} scale={s}>
      <mesh castShadow receiveShadow position={[0, 0.7, 0]}>
        <cylinderGeometry args={[0.08, 0.16, 1.4, 7]} />
        <WoodMat color="#5a3b22" />
      </mesh>
      {boughs.map(([radius, height, y, color]) => (
        <mesh key={y} castShadow position={[0, y, 0]}>
          <coneGeometry args={[radius, height, 8]} />
          <meshStandardMaterial color={color} roughness={0.82} metalness={0.02} />
        </mesh>
      ))}
      <mesh position={[0, 3.22, 0]}>
        <coneGeometry args={[0.12, 0.28, 6]} />
        <meshStandardMaterial color="#e7f2ea" roughness={0.7} />
      </mesh>
    </group>
  );
}

function FarmPlot({
  x,
  z,
  rot = 0,
  farmTex,
}: {
  x: number;
  z: number;
  rot?: number;
  farmTex: THREE.Texture;
}) {
  return (
    <group position={[x, 0, z]} rotation={[0, rot, 0]}>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]} receiveShadow>
        <planeGeometry args={[3.2, 4.2]} />
        <meshStandardMaterial map={farmTex} roughness={0.95} metalness={0.02} />
      </mesh>
      <group position={[1.6, 0, -1.6]}>
        <mesh castShadow position={[0, 0.9, 0]}>
          <cylinderGeometry args={[0.55, 0.7, 1.8, 10]} />
          <StoneMat />
        </mesh>
        <mesh castShadow position={[0, 1.95, 0]}>
          <coneGeometry args={[0.75, 0.55, 8]} />
          <RoofMat />
        </mesh>
      </group>
    </group>
  );
}

function HouseDecor({ x, z, rot = 0 }: { x: number; z: number; rot?: number }) {
  return (
    <group position={[x, 0, z]} rotation={[0, rot, 0]}>
      <mesh castShadow receiveShadow position={[0, 0.55, 0]}>
        <boxGeometry args={[1.4, 1.1, 1.2]} />
        <StoneMat color="#e8e0d4" />
      </mesh>
      <mesh castShadow position={[0, 1.25, 0]} rotation={[0, Math.PI / 4, 0]}>
        <coneGeometry args={[1.1, 0.7, 4]} />
        <RoofMat />
      </mesh>
      <mesh position={[0.35, 0.55, 0.61]}>
        <boxGeometry args={[0.28, 0.28, 0.04]} />
        <meshStandardMaterial
          color="#1a2830"
          roughness={0.2}
          metalness={0.6}
          emissive="#3a6080"
          emissiveIntensity={0.15}
        />
      </mesh>
    </group>
  );
}

function TownHallMesh({ accent }: { accent: string }) {
  return <StoneHall accent={accent} />;
}

function BarracksMesh({ accent }: { accent: string }) {
  return <LongBarracks accent={accent} />;
}

function HouseMesh({ accent }: { accent: string }) {
  return <TimberHouse accent={accent} />;
}

const WHO: Record<string, string> = {
  worker: "Villager",
  explorer: "Explorer",
  healer: "Priest",
  geologist: "Geologist",
  militia: "Militia",
  spearman: "Spearman",
  man_at_arms: "Man-at-Arms",
  archer: "Archer",
  skirmisher: "Skirmisher",
  crossbowman: "Crossbowman",
  hand_cannon: "Hand Cannon",
  scout_cav: "Scout",
  knight: "Knight",
  camel_rider: "Camel Rider",
  war_elephant: "Elephant",
  ram: "Ram",
  mangonel: "Mangonel",
  scorpion: "Scorpion",
  trebuchet: "Trebuchet",
  fishing_boat: "Fishing Boat",
  transport: "Ferry",
  war_galley: "War Galley",
  trade_cart: "Trade Cart",
  town_hall: "Town Hall",
  house: "House",
  farm: "Farm",
  mill: "Mill",
  granary: "Granary",
  storage_pit: "Storage Pit",
  lumber_camp: "Lumber Camp",
  mining_camp: "Mining Camp",
  barracks: "Barracks",
  archery_range: "Archery Range",
  stable: "Stable",
  blacksmith: "Blacksmith",
  university: "University",
  market: "Market",
  dock: "Dock",
  watch_tower: "Tower",
  siege_workshop: "Siege Workshop",
  keep: "Keep",
  church: "Church",
  wonder: "Wonder",
  sheep: "Sheep",
  chicken: "Chicken",
  cow: "Cow",
  goat: "Goat",
  duck: "Duck",
  deer: "Deer",
  coyote: "Coyote",
  gorilla: "Gorilla",
  lion: "Lion",
  beach_kid: "Child",
  ruin: "Ruin",
  artifact: "Artifact",
  chrono: "Relic",
};

const nameTex = new Map<string, THREE.CanvasTexture>();

function nameTexture(label: string): THREE.CanvasTexture {
  const hit = nameTex.get(label);
  if (hit) return hit;
  const c = document.createElement("canvas");
  c.width = 512;
  c.height = 96;
  const ctx = c.getContext("2d");
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  if (!ctx) return tex;
  ctx.clearRect(0, 0, 512, 96);
  let size = 62;
  ctx.font = `700 ${size}px sans-serif`;
  while (ctx.measureText(label).width > 480 && size > 28) {
    size -= 4;
    ctx.font = `700 ${size}px sans-serif`;
  }
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.lineWidth = 12;
  ctx.strokeStyle = "#0c140f";
  ctx.strokeText(label, 256, 50);
  ctx.fillStyle = "#fff6df";
  ctx.fillText(label, 256, 50);
  tex.needsUpdate = true;
  nameTex.set(label, tex);
  return tex;
}

/** Name over the head, health bar under the name. */
function WhoPlate({ kind, hp, maxHp, y }: { kind: string; hp: number; maxHp: number; y: number }) {
  const label = WHO[kind];
  const ratio = Math.max(0, Math.min(1, maxHp > 0 ? hp / maxHp : 1));
  const w = 1.15;
  const h = 0.16;
  const fillW = Math.max(0.05, w * ratio);
  const color = ratio >= 0.9 ? "#3dff7a" : ratio >= 0.5 ? "#d6e04a" : "#e23b3b";
  const nameW = label ? Math.min(2.6, Math.max(1.15, label.length * 0.2)) : 0;
  return (
    <Billboard follow position={[0, y, 0]}>
      {label ? (
        <mesh position={[0, 0.28, 0]} renderOrder={11} raycast={() => null}>
          <planeGeometry args={[nameW, 0.34]} />
          <meshBasicMaterial map={nameTexture(label)} transparent depthTest={false} depthWrite={false} />
        </mesh>
      ) : null}
      {maxHp > 1 ? (
        <>
          <mesh position={[0, 0, -0.01]} renderOrder={8} raycast={() => null}>
            <planeGeometry args={[w + 0.12, h + 0.08]} />
            <meshBasicMaterial color="#102616" depthTest={false} depthWrite={false} />
          </mesh>
          <mesh position={[-(w - fillW) / 2, 0, 0.02]} renderOrder={9} raycast={() => null}>
            <planeGeometry args={[fillW, h]} />
            <meshBasicMaterial color={color} depthTest={false} depthWrite={false} />
          </mesh>
        </>
      ) : null}
    </Billboard>
  );
}

/** Classic RTS job bar — dark track + green→yellow fill, camera-facing. */
function WorkProgressBar({ progress }: { progress: number }) {
  const p = Math.max(0, Math.min(1, progress));
  const fill = p < 0.55 ? "#6bcb77" : p < 0.85 ? "#d4c84a" : "#e8d060";
  const w = 0.72;
  const h = 0.1;
  const fillW = Math.max(0.02, w * p);
  return (
    <Billboard follow position={[0, 1.45, 0]}>
      <mesh position={[0, 0, 0]}>
        <planeGeometry args={[w + 0.06, h + 0.05]} />
        <meshBasicMaterial color="#1a120c" transparent opacity={0.92} depthTest={false} />
      </mesh>
      <mesh position={[-(w - fillW) / 2, 0, 0.01]}>
        <planeGeometry args={[fillW, h]} />
        <meshBasicMaterial color={fill} depthTest={false} />
      </mesh>
    </Billboard>
  );
}

function WorkerMesh({
  accent,
  bob,
  gait,
}: {
  accent: string;
  bob: number;
  gait?: React.MutableRefObject<Gait>;
  workProgress?: number;
}) {
  return (
    <group>
      <Person role="worker" accent={accent} bob={bob} gait={gait} />
    </group>
  );
}

function ChurchMesh({ accent }: { accent: string }) {
  return (
    <group>
      <mesh position={[0, 0.55, 0]} castShadow>
        <boxGeometry args={[1.55, 1.1, 1.2]} />
        <meshStandardMaterial color="#efe6d4" roughness={0.86} />
      </mesh>
      <mesh position={[0, 1.22, 0]} castShadow>
        <boxGeometry args={[1.7, 0.14, 1.32]} />
        <meshStandardMaterial color="#d4653a" roughness={0.7} />
      </mesh>
      <mesh position={[0, 1.72, 0]} castShadow>
        <boxGeometry args={[0.46, 0.78, 0.46]} />
        <meshStandardMaterial color="#efe6d4" roughness={0.86} />
      </mesh>
      <mesh position={[0, 2.22, 0]}>
        <boxGeometry args={[0.08, 0.34, 0.08]} />
        <meshStandardMaterial color="#e8d48a" metalness={0.35} roughness={0.4} />
      </mesh>
      <mesh position={[0, 2.3, 0]}>
        <boxGeometry args={[0.24, 0.07, 0.07]} />
        <meshStandardMaterial color="#e8d48a" metalness={0.35} roughness={0.4} />
      </mesh>
      <mesh position={[0, 0.62, 0.62]}>
        <boxGeometry args={[0.32, 0.55, 0.06]} />
        <meshStandardMaterial color="#6a4030" />
      </mesh>
      <mesh position={[0, 1.42, 0]}>
        <boxGeometry args={[0.52, 0.08, 0.52]} />
        <meshStandardMaterial color={accent} />
      </mesh>
    </group>
  );
}

function MilitiaMesh({ accent, bob, gait }: { accent: string; bob: number; gait?: React.MutableRefObject<Gait> }) {
  return <Person role="militia" accent={accent} bob={bob} gait={gait} />;
}

function CampMesh({ accent, tint }: { accent: string; tint: string }) {
  return <GabledYard accent={accent} wall={tint} roof="#8a4a28" />;
}

function ArcheryMesh({ accent }: { accent: string }) {
  return (
    <group>
      <mesh castShadow receiveShadow position={[0, 0.55, 0]}>
        <boxGeometry args={[2.2, 1.05, 1.45]} />
        <WoodMat color="#7a5530" />
      </mesh>
      <GableRoof w={2.55} d={1.8} rise={0.55} y={1.08} color="#6a4030" />
      {[-0.7, 0.7].map((x) => (
        <mesh key={x} castShadow position={[x, 0.7, 0.8]}>
          <cylinderGeometry args={[0.06, 0.07, 1.5, 6]} />
          <WoodMat color="#4a3018" />
        </mesh>
      ))}
      <mesh position={[0, 1.75, 0]}>
        <boxGeometry args={[0.28, 0.16, 0.03]} />
        <meshStandardMaterial color={accent} roughness={0.45} />
      </mesh>
    </group>
  );
}

function StableMesh({ accent }: { accent: string }) {
  return (
    <group>
      <mesh castShadow receiveShadow position={[0, 0.58, 0]}>
        <boxGeometry args={[2.4, 1.1, 1.7]} />
        <WoodMat color="#8b6540" />
      </mesh>
      <GableRoof w={2.75} d={2.05} rise={0.58} y={1.13} color="#7a3e22" />
      <mesh position={[0, 0.5, 0.88]}>
        <boxGeometry args={[0.85, 0.85, 0.06]} />
        <WoodMat color="#3a2818" />
      </mesh>
      <mesh position={[0.85, 1.85, 0]}>
        <boxGeometry args={[0.28, 0.16, 0.03]} />
        <meshStandardMaterial color={accent} roughness={0.45} />
      </mesh>
    </group>
  );
}

function BlacksmithMesh({ accent }: { accent: string }) {
  return (
    <group>
      <mesh castShadow receiveShadow position={[0, 0.55, 0]}>
        <boxGeometry args={[1.9, 1.05, 1.5]} />
        <StoneMat color="#8a8070" />
      </mesh>
      <GableRoof w={2.2} d={1.85} rise={0.5} y={1.08} color="#5c4038" />
      <mesh castShadow position={[0.45, 1.45, 0.1]}>
        <boxGeometry args={[0.28, 0.7, 0.28]} />
        <StoneMat color="#6a6058" />
      </mesh>
      <mesh position={[0.55, 0.55, 0.78]}>
        <boxGeometry args={[0.22, 0.22, 0.05]} />
        <meshStandardMaterial color="#ff6020" emissive="#ff4010" emissiveIntensity={0.55} />
      </mesh>
      <mesh position={[-0.55, 1.7, 0]}>
        <boxGeometry args={[0.26, 0.14, 0.03]} />
        <meshStandardMaterial color={accent} roughness={0.45} />
      </mesh>
    </group>
  );
}

function UniversityMesh({ accent }: { accent: string }) {
  return (
    <group>
      <mesh castShadow receiveShadow position={[0, 0.7, 0]}>
        <boxGeometry args={[2.15, 1.35, 1.9]} />
        <StoneMat color="#e0d8c8" />
      </mesh>
      <GableRoof w={2.5} d={2.25} rise={0.62} y={1.38} color="#8a4030" />
      <mesh castShadow position={[0, 1.85, 0]}>
        <cylinderGeometry args={[0.42, 0.5, 0.55, 12]} />
        <StoneMat color="#d0c8b8" />
      </mesh>
      <mesh castShadow position={[0, 2.35, 0]}>
        <sphereGeometry args={[0.48, 14, 10]} />
        <meshStandardMaterial map={roofTex()} color="#3a5878" roughness={0.45} metalness={0.15} />
      </mesh>
      <mesh position={[0, 2.95, 0]}>
        <boxGeometry args={[0.22, 0.14, 0.03]} />
        <meshStandardMaterial color={accent} roughness={0.45} />
      </mesh>
    </group>
  );
}

function MarketMesh({ accent }: { accent: string }) {
  return (
    <group>
      <mesh castShadow receiveShadow position={[0, 0.45, 0]}>
        <boxGeometry args={[2.15, 0.85, 1.55]} />
        <WoodMat color="#a08050" />
      </mesh>
      <GableRoof w={2.5} d={1.9} rise={0.62} y={0.88} color="#c04040" />
      {[-0.7, 0, 0.7].map((x) => (
        <mesh key={x} position={[x, 0.4, 0.8]}>
          <boxGeometry args={[0.42, 0.28, 0.04]} />
          <meshStandardMaterial color="#e8d090" roughness={0.7} />
        </mesh>
      ))}
      <mesh position={[0.7, 1.6, 0]}>
        <boxGeometry args={[0.26, 0.14, 0.03]} />
        <meshStandardMaterial color={accent} roughness={0.45} />
      </mesh>
    </group>
  );
}

function DockMesh({ accent }: { accent: string }) {
  return (
    <group>
      <mesh castShadow receiveShadow position={[0, 0.15, 0]}>
        <boxGeometry args={[2.4, 0.3, 1.8]} />
        <WoodMat color="#5a4030" />
      </mesh>
      <mesh castShadow position={[0, 0.55, -0.6]}>
        <boxGeometry args={[1.2, 0.8, 0.6]} />
        <WoodMat color="#6a4838" />
      </mesh>
      {[-0.9, 0, 0.9].map((x, i) => (
        <mesh key={i} castShadow position={[x, 0.05, 0.7]}>
          <cylinderGeometry args={[0.08, 0.1, 0.5, 6]} />
          <WoodMat />
        </mesh>
      ))}
      <mesh position={[0.4, 1.1, -0.6]}>
        <boxGeometry args={[0.1, 0.3, 0.1]} />
        <meshStandardMaterial color={accent} roughness={0.4} metalness={0.35} />
      </mesh>
    </group>
  );
}

function TowerMesh({ accent }: { accent: string }) {
  return <StoneTower accent={accent} />;
}

function FortTrim({ accent, y = 0.22, w = 1.05, d = 0.52 }: { accent: string; y?: number; w?: number; d?: number }) {
  return (
    <mesh castShadow position={[0, y, 0]}>
      <boxGeometry args={[w, 0.12, d]} />
      <meshStandardMaterial color={accent} roughness={0.45} metalness={0.2} />
    </mesh>
  );
}

function CrenelRow({
  alongX,
  y,
  z,
  count,
  color,
}: {
  alongX: boolean;
  y: number;
  z: number;
  count: number;
  color: string;
}) {
  const items = [];
  for (let i = 0; i < count; i++) {
    const t = (i / Math.max(1, count - 1)) * 0.85 - 0.425;
    items.push(
      <mesh
        key={i}
        castShadow
        position={alongX ? [t, y, z] : [z, y, t]}
      >
        <boxGeometry args={alongX ? [0.18, 0.28, 0.16] : [0.16, 0.28, 0.18]} />
        <StoneMat color={color} />
      </mesh>
    );
  }
  return <group>{items}</group>;
}

function CornerTowerMesh({ accent, tier }: { accent: string; tier: "wood" | "stone" | "fort" }) {
  const h = tier === "fort" ? 2.4 : tier === "stone" ? 2.0 : 1.55;
  const wood = tier === "wood";
  return (
    <group>
      <mesh castShadow receiveShadow position={[0, h * 0.45, 0]}>
        <boxGeometry args={[0.85, h * 0.9, 0.85]} />
        {wood ? <WoodMat color="#5a4030" /> : <StoneMat color={tier === "fort" ? "#9a9488" : "#b8b0a4"} />}
      </mesh>
      <FortTrim accent={accent} y={0.28} w={0.9} d={0.9} />
      <mesh castShadow position={[0, h * 0.95, 0]}>
        <boxGeometry args={[0.95, 0.18, 0.95]} />
        {wood ? <WoodMat color="#6a4838" /> : <StoneMat color="#c8c0b4" />}
      </mesh>
      {!wood && (
        <mesh castShadow position={[0, h * 1.12, 0]}>
          <boxGeometry args={[1.05, 0.16, 1.05]} />
          <meshStandardMaterial color="#c45532" roughness={0.72} />
        </mesh>
      )}
      {[-0.28, 0.28].map((x) =>
        [-0.28, 0.28].map((z) => (
          <mesh key={`${x}:${z}`} castShadow position={[x, h * 1.08, z]}>
            <boxGeometry args={[0.22, 0.28, 0.22]} />
            {wood ? <WoodMat color="#4a3020" /> : <StoneMat color="#a89888" />}
          </mesh>
        ))
      )}
    </group>
  );
}

function WallMesh({
  accent,
  mask = 0,
  tier = "stone",
}: {
  accent: string;
  mask?: number;
  tier?: "wood" | "stone" | "fort";
}) {
  const { fortDegree, fortIsCorner, fortStraightYaw, fortCornerYaw, FORT_PX, FORT_NX, FORT_PZ, FORT_NZ } =
    requireFortBits();
  const deg = fortDegree(mask);
  const isCorner = fortIsCorner(mask);
  const isEnd = deg <= 1;
  const yaw = isCorner
    ? fortCornerYaw(mask)
    : fortStraightYaw(mask) ||
      (mask & (FORT_PZ | FORT_NZ) ? Math.PI / 2 : 0);
  const wood = tier === "wood";
  const bodyH = wood ? 1.15 : tier === "fort" ? 1.7 : 1.48;
  const stoneCol = tier === "fort" ? "#9a9488" : "#b0aaa0";

  if (isCorner || isEnd || deg >= 3) {
    return (
      <group rotation={[0, isCorner ? yaw : 0, 0]}>
        <CornerTowerMesh accent={accent} tier={tier} />
        {/* Stubs toward neighbors */}
        {(mask & FORT_PX) !== 0 && (
          <group position={[0.55, 0, 0]}>
            <mesh castShadow receiveShadow position={[0.2, bodyH * 0.45, 0]}>
              <boxGeometry args={[0.55, bodyH, 0.48]} />
              {wood ? <WoodMat color="#5a4030" /> : <StoneMat color={stoneCol} />}
            </mesh>
          </group>
        )}
        {(mask & FORT_NX) !== 0 && (
          <group position={[-0.55, 0, 0]}>
            <mesh castShadow receiveShadow position={[-0.2, bodyH * 0.45, 0]}>
              <boxGeometry args={[0.55, bodyH, 0.48]} />
              {wood ? <WoodMat color="#5a4030" /> : <StoneMat color={stoneCol} />}
            </mesh>
          </group>
        )}
        {(mask & FORT_PZ) !== 0 && (
          <group position={[0, 0, 0.55]}>
            <mesh castShadow receiveShadow position={[0, bodyH * 0.45, 0.2]}>
              <boxGeometry args={[0.48, bodyH, 0.55]} />
              {wood ? <WoodMat color="#5a4030" /> : <StoneMat color={stoneCol} />}
            </mesh>
          </group>
        )}
        {(mask & FORT_NZ) !== 0 && (
          <group position={[0, 0, -0.55]}>
            <mesh castShadow receiveShadow position={[0, bodyH * 0.45, -0.2]}>
              <boxGeometry args={[0.48, bodyH, 0.55]} />
              {wood ? <WoodMat color="#5a4030" /> : <StoneMat color={stoneCol} />}
            </mesh>
          </group>
        )}
      </group>
    );
  }

  // Straight segment — fills full cell so neighbors abut
  return (
    <group rotation={[0, yaw, 0]}>
      <mesh receiveShadow position={[0, 0.07, 0]}>
        <boxGeometry args={[1.08, 0.14, wood ? 0.5 : 0.72]} />
        {wood ? <WoodMat color="#3a2818" /> : <StoneMat color="#6e675c" />}
      </mesh>
      <mesh castShadow receiveShadow position={[0, bodyH * 0.45, 0]}>
        <boxGeometry args={[1.05, bodyH, wood ? 0.42 : 0.62]} />
        {wood ? <WoodMat color="#5a4030" /> : <StoneMat color={stoneCol} />}
      </mesh>
      <FortTrim accent={accent} y={0.22} w={1.05} d={wood ? 0.48 : 0.68} />
      {!wood && (
        <>
          <mesh position={[0, bodyH * 0.42, 0.28]}>
            <boxGeometry args={[0.96, 0.16, 0.08]} />
            <StoneMat color="#d8cfc2" />
          </mesh>
          <mesh position={[0, bodyH * 0.72, 0.28]}>
            <boxGeometry args={[0.96, 0.16, 0.08]} />
            <StoneMat color="#d8cfc2" />
          </mesh>
          <mesh castShadow position={[0, bodyH + 0.08, 0]}>
            <boxGeometry args={[1.12, 0.16, 0.78]} />
            <meshStandardMaterial color="#c45532" roughness={0.72} />
          </mesh>
          <mesh castShadow position={[0, bodyH + 0.22, 0]}>
            <boxGeometry args={[0.7, 0.14, 0.42]} />
            <meshStandardMaterial color="#a84428" roughness={0.7} />
          </mesh>
        </>
      )}
      {wood &&
        [-0.35, -0.12, 0.12, 0.35].map((x, i) => (
          <mesh key={i} castShadow position={[x, bodyH * 0.95, 0]}>
            <boxGeometry args={[0.12, 0.35, 0.12]} />
            <WoodMat color="#4a3020" />
          </mesh>
        ))}
    </group>
  );
}

// Lightweight fort bit helpers mirrored for client (no import cycle)
function requireFortBits() {
  const FORT_PZ = 1;
  const FORT_PX = 2;
  const FORT_NZ = 4;
  const FORT_NX = 8;
  const fortDegree = (mask: number) => {
    let n = 0;
    let m = mask & 15;
    while (m) {
      n += m & 1;
      m >>= 1;
    }
    return n;
  };
  const fortIsCorner = (mask: number) => {
    if (fortDegree(mask) !== 2) return false;
    const opp =
      (mask & FORT_PZ && mask & FORT_NZ) || (mask & FORT_PX && mask & FORT_NX);
    return !opp;
  };
  const fortStraightYaw = (mask: number) => {
    if (
      (mask & FORT_PZ || mask & FORT_NZ) &&
      !(mask & FORT_PX) &&
      !(mask & FORT_NX)
    )
      return Math.PI / 2;
    return 0;
  };
  const fortCornerYaw = (mask: number) => {
    const pz = !!(mask & FORT_PZ);
    const px = !!(mask & FORT_PX);
    const nz = !!(mask & FORT_NZ);
    const nx = !!(mask & FORT_NX);
    if (px && pz) return 0;
    if (pz && nx) return Math.PI / 2;
    if (nx && nz) return Math.PI;
    if (nz && px) return -Math.PI / 2;
    return 0;
  };
  return {
    fortDegree,
    fortIsCorner,
    fortStraightYaw,
    fortCornerYaw,
    FORT_PX,
    FORT_NX,
    FORT_PZ,
    FORT_NZ,
  };
}

function GateMesh({
  accent,
  mask = 0,
  open = false,
}: {
  accent: string;
  mask?: number;
  open?: boolean;
}) {
  const { fortStraightYaw, FORT_PZ, FORT_NZ } = requireFortBits();
  const yaw =
    fortStraightYaw(mask) ||
    (mask & (FORT_PZ | FORT_NZ) ? Math.PI / 2 : 0);
  return (
    <group rotation={[0, yaw, 0]}>
      {/* Twin towers */}
      {[-0.7, 0.7].map((x) => (
        <group key={x} position={[x, 0, 0]}>
          <mesh castShadow receiveShadow position={[0, 1.1, 0]}>
            <boxGeometry args={[0.55, 2.2, 0.7]} />
            <StoneMat color="#b0aaa0" />
          </mesh>
          <FortTrim accent={accent} y={0.25} w={0.6} d={0.75} />
          <mesh castShadow position={[0, 2.3, 0]}>
            <boxGeometry args={[0.65, 0.2, 0.8]} />
            <StoneMat color="#c0b8ac" />
          </mesh>
          {[-0.15, 0.15].map((dx) => (
            <mesh key={dx} castShadow position={[dx, 2.5, 0.2]}>
              <boxGeometry args={[0.18, 0.28, 0.18]} />
              <StoneMat color="#a89888" />
            </mesh>
          ))}
          {/* Flag */}
          <mesh position={[0, 2.85, 0]}>
            <boxGeometry args={[0.06, 0.45, 0.06]} />
            <WoodMat />
          </mesh>
          <mesh position={[0.12, 2.95, 0]}>
            <boxGeometry args={[0.22, 0.14, 0.04]} />
            <meshStandardMaterial color={accent} />
          </mesh>
        </group>
      ))}
      {/* Arch lintel */}
      <mesh castShadow position={[0, 1.85, 0]}>
        <boxGeometry args={[1.0, 0.35, 0.55]} />
        <StoneMat color="#c0b8ac" />
      </mesh>
      {/* Portcullis */}
      {!open &&
        [-0.25, -0.08, 0.08, 0.25].map((x, i) => (
          <mesh key={i} position={[x, 0.85, 0]}>
            <boxGeometry args={[0.06, 1.5, 0.06]} />
            <meshStandardMaterial color="#2a2a30" metalness={0.7} roughness={0.35} />
          </mesh>
        ))}
      {!open &&
        [0.4, 0.7, 1.0].map((y, i) => (
          <mesh key={`h${i}`} position={[0, y, 0]}>
            <boxGeometry args={[0.7, 0.05, 0.05]} />
            <meshStandardMaterial color="#2a2a30" metalness={0.7} roughness={0.35} />
          </mesh>
        ))}
    </group>
  );
}

function KeepMesh({ accent }: { accent: string }) {
  return <StoneKeep accent={accent} />;
}

function SiegeMesh({ accent }: { accent: string }) {
  return (
    <group>
      <mesh castShadow receiveShadow position={[0, 0.5, 0]}>
        <boxGeometry args={[2.15, 1.0, 1.65]} />
        <WoodMat color="#6a5038" />
      </mesh>
      <GableRoof w={2.45} d={1.95} rise={0.48} y={1.0} color="#5a3820" />
      <mesh position={[0.7, 1.6, 0]}>
        <boxGeometry args={[0.26, 0.14, 0.03]} />
        <meshStandardMaterial color={accent} roughness={0.45} />
      </mesh>
    </group>
  );
}

function FarmMesh({ farmTex }: { farmTex: THREE.Texture }) {
  return (
    <group>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0.2]} receiveShadow>
        <planeGeometry args={[2.7, 2.3]} />
        <meshStandardMaterial map={farmTex} roughness={0.95} metalness={0.02} />
      </mesh>
      {Array.from({ length: 7 }, (_, i) => (
        <mesh key={i} rotation={[-Math.PI / 2, 0, 0]} position={[0.15, 0.045, -0.7 + i * 0.28]}>
          <planeGeometry args={[2.15, 0.1]} />
          <meshStandardMaterial color={i % 2 ? "#5f8a32" : "#c6a15c"} roughness={1} />
        </mesh>
      ))}
      <mesh castShadow receiveShadow position={[-0.9, 0.4, -0.2]}>
        <boxGeometry args={[0.8, 0.7, 0.7]} />
        <WoodMat color="#6a4c32" />
      </mesh>
      <GableRoof w={1.05} d={0.95} rise={0.42} y={0.75} color="#6a5e52" />
    </group>
  );
}

function ArcherMesh({ accent, bob, gait, role = "archer" }: { accent: string; bob: number; gait?: React.MutableRefObject<Gait>; role?: string }) {
  return <Person role={role} accent={accent} bob={bob} gait={gait} />;
}

function SpearmanMesh({ accent, bob, gait }: { accent: string; bob: number; gait?: React.MutableRefObject<Gait> }) {
  return <Person role="spear" accent={accent} bob={bob} gait={gait} />;
}

function ManAtArmsMesh({ accent, bob, gait }: { accent: string; bob: number; gait?: React.MutableRefObject<Gait> }) {
  return <Person role="man" accent={accent} bob={bob} gait={gait} />;
}

function CrossbowMesh({ accent, bob, gait }: { accent: string; bob: number; gait?: React.MutableRefObject<Gait> }) {
  return <Person role="crossbow" accent={accent} bob={bob} gait={gait} />;
}

function HandCannonMesh({ accent, bob, gait }: { accent: string; bob: number; gait?: React.MutableRefObject<Gait> }) {
  return <Person role="cannon" accent={accent} bob={bob} gait={gait} />;
}

function LighthouseMesh() {
  return (
    <group>
      <mesh castShadow receiveShadow position={[0, 0.4, 0]}>
        <cylinderGeometry args={[0.55, 0.7, 0.8, 10]} />
        <StoneMat color="#d8d2c6" />
      </mesh>
      <mesh castShadow position={[0, 1.5, 0]}>
        <cylinderGeometry args={[0.32, 0.42, 1.6, 10]} />
        <meshStandardMaterial color="#f2f0ea" roughness={0.7} />
      </mesh>
      <mesh castShadow position={[0, 2.45, 0]}>
        <cylinderGeometry args={[0.38, 0.38, 0.35, 10]} />
        <meshStandardMaterial color="#c0392b" roughness={0.55} />
      </mesh>
      <mesh position={[0, 2.75, 0]}>
        <sphereGeometry args={[0.22, 12, 10]} />
        <meshStandardMaterial color="#ffe08a" emissive="#ffcc55" emissiveIntensity={1.2} />
      </mesh>
      <mesh castShadow position={[0, 3.05, 0]}>
        <coneGeometry args={[0.4, 0.35, 8]} />
        <RoofMat color="#4a4038" />
      </mesh>
    </group>
  );
}

function OilWellMesh({ kind, accent }: { kind: string; accent: string }) {
  const drilled = kind === "oil_well";
  return (
    <group>
      <mesh castShadow receiveShadow position={[0, 0.08, 0]}>
        <cylinderGeometry args={[0.55, 0.65, 0.16, 10]} />
        <meshStandardMaterial color={drilled ? "#3a3a38" : "#5a4a30"} roughness={0.9} />
      </mesh>
      {drilled ? (
        <>
          <mesh castShadow position={[0, 0.9, 0]}>
            <cylinderGeometry args={[0.08, 0.1, 1.6, 8]} />
            <meshStandardMaterial color="#6a7078" metalness={0.7} roughness={0.35} />
          </mesh>
          <mesh castShadow position={[0.35, 1.1, 0]} rotation={[0, 0, 0.6]}>
            <boxGeometry args={[0.12, 1.1, 0.12]} />
            <meshStandardMaterial color="#4a5058" metalness={0.6} roughness={0.4} />
          </mesh>
          <mesh position={[0, 1.75, 0]}>
            <boxGeometry args={[0.1, 0.25, 0.1]} />
            <meshStandardMaterial color={accent} />
          </mesh>
        </>
      ) : (
        <mesh castShadow position={[0, 0.25, 0]}>
          <sphereGeometry args={[0.28, 10, 8]} />
          <meshStandardMaterial
            color={kind === "gas_seep" ? "#7a8a60" : "#2a2a28"}
            roughness={0.85}
            emissive={kind === "gas_seep" ? "#406020" : "#1a1008"}
            emissiveIntensity={0.25}
          />
        </mesh>
      )}
    </group>
  );
}

function ScenicPersonMesh({ kind, gait }: { kind: string; gait?: React.MutableRefObject<Gait> }) {
  const kid = kind === "beach_kid";
  const tunic = kid ? "#3db8e8" : "#5a6570";
  const pants = kid ? "#f0d060" : "#3a4048";
  const leftLeg = useRef<THREE.Group>(null);
  const rightLeg = useRef<THREE.Group>(null);
  useFrame(() => {
    const amp = gait?.current.amp || 0;
    const phase = gait?.current.phase || 0;
    const swing = Math.sin(phase) * amp * 0.7;
    if (leftLeg.current) leftLeg.current.rotation.x = swing;
    if (rightLeg.current) rightLeg.current.rotation.x = -swing;
  });
  return (
    <group>
      <group ref={rightLeg} position={[0.06, kid ? 0.23 : 0.35, 0]}>
        <mesh castShadow position={[0, kid ? -0.11 : -0.17, 0]}>
          <cylinderGeometry args={[kid ? 0.035 : 0.045, kid ? 0.04 : 0.05, kid ? 0.22 : 0.34, 6]} />
          <meshStandardMaterial color={pants} roughness={0.8} />
        </mesh>
      </group>
      <group ref={leftLeg} position={[-0.06, kid ? 0.23 : 0.35, 0]}>
        <mesh castShadow position={[0, kid ? -0.11 : -0.17, 0]}>
          <cylinderGeometry args={[kid ? 0.035 : 0.045, kid ? 0.04 : 0.05, kid ? 0.22 : 0.34, 6]} />
          <meshStandardMaterial color={pants} roughness={0.8} />
        </mesh>
      </group>
      {/* torso */}
      <mesh castShadow position={[0, kid ? 0.32 : 0.48, 0]}>
        <capsuleGeometry args={[kid ? 0.09 : 0.12, kid ? 0.14 : 0.22, 4, 8]} />
        <meshStandardMaterial color={tunic} roughness={0.7} />
      </mesh>
      {/* head */}
      <mesh castShadow position={[0, kid ? 0.52 : 0.76, 0]}>
        <sphereGeometry args={[kid ? 0.1 : 0.125, 12, 10]} />
        <meshStandardMaterial color="#e0c4a0" roughness={0.65} />
      </mesh>
      {/* hair */}
      <mesh position={[0, kid ? 0.58 : 0.84, -0.02]}>
        <sphereGeometry args={[kid ? 0.09 : 0.11, 10, 8]} />
        <meshStandardMaterial color={kid ? "#c47828" : "#3a3028"} roughness={0.9} />
      </mesh>
      {kid ? (
        // beach ball
        <mesh castShadow position={[0.22, 0.18, 0.1]}>
          <sphereGeometry args={[0.08, 10, 8]} />
          <meshStandardMaterial color="#e85a5a" roughness={0.55} />
        </mesh>
      ) : (
        // rock hammer / drill rod
        <>
          <mesh castShadow position={[0.24, 0.5, 0.05]} rotation={[0.35, 0, -0.45]}>
            <cylinderGeometry args={[0.022, 0.028, 0.55, 6]} />
            <meshStandardMaterial color="#8a8e96" metalness={0.55} roughness={0.35} />
          </mesh>
          <mesh castShadow position={[0.4, 0.28, 0.12]} rotation={[0.2, 0.3, 0.1]}>
            <boxGeometry args={[0.08, 0.06, 0.1]} />
            <meshStandardMaterial color="#6a7078" metalness={0.6} roughness={0.4} />
          </mesh>
          <mesh castShadow position={[0, 0.72, 0.02]}>
            <boxGeometry args={[0.16, 0.05, 0.12]} />
            <meshStandardMaterial color="#c9a24a" roughness={0.55} />
          </mesh>
        </>
      )}
    </group>
  );
}


function AnimalMesh({ kind, gait }: { kind: string; gait?: React.MutableRefObject<Gait> }) {
  return <BeastMesh kind={kind} gait={gait} />;
}

function CavalryMesh({
  accent,
  bob,
  knight,
  gait,
}: {
  accent: string;
  bob: number;
  knight?: boolean;
  gait?: React.MutableRefObject<Gait>;
}) {
  return <HorseRider accent={accent} bob={bob} knight={knight} gait={gait} />;
}

function CamelMesh({ accent, bob, gait }: { accent: string; bob: number; gait?: React.MutableRefObject<Gait> }) {
  return <CamelRider accent={accent} bob={bob} gait={gait} />;
}

function ScorpionMesh({ accent, bob }: { accent: string; bob: number }) {
  return (
    <group position={[0, bob * 0.3, 0]}>
      <mesh castShadow position={[0, 0.22, 0]}>
        <boxGeometry args={[0.85, 0.28, 0.55]} />
        <WoodMat />
      </mesh>
      <mesh castShadow position={[0.35, 0.4, 0]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.06, 0.07, 1.1, 8]} />
        <WoodMat color="#4a3020" />
      </mesh>
      <mesh castShadow position={[0.85, 0.4, 0]}>
        <boxGeometry args={[0.12, 0.08, 0.08]} />
        <meshStandardMaterial color="#9098a0" metalness={0.7} />
      </mesh>
      <mesh position={[0, 0.55, 0]}>
        <boxGeometry args={[0.08, 0.18, 0.08]} />
        <meshStandardMaterial color={accent} />
      </mesh>
    </group>
  );
}

function TrebuchetMesh({ accent, bob }: { accent: string; bob: number }) {
  return (
    <group position={[0, bob * 0.2, 0]}>
      <mesh castShadow position={[0, 0.35, 0]}>
        <boxGeometry args={[1.2, 0.4, 0.8]} />
        <WoodMat color="#5a4030" />
      </mesh>
      <mesh castShadow position={[0, 1.0, 0]}>
        <boxGeometry args={[0.14, 1.4, 0.14]} />
        <WoodMat color="#4a3020" />
      </mesh>
      <mesh castShadow position={[-0.55, 1.55, 0]} rotation={[0, 0, 0.5]}>
        <boxGeometry args={[0.12, 1.3, 0.12]} />
        <WoodMat />
      </mesh>
      <mesh castShadow position={[-1.05, 1.0, 0]}>
        <sphereGeometry args={[0.22, 8, 6]} />
        <StoneMat color="#777" />
      </mesh>
      <mesh position={[0.35, 0.7, 0]}>
        <boxGeometry args={[0.1, 0.22, 0.1]} />
        <meshStandardMaterial color={accent} />
      </mesh>
    </group>
  );
}

function ShipHull({
  len,
  beam,
  color,
}: {
  len: number;
  beam: number;
  color: string;
}) {
  return (
    <group>
      <mesh castShadow receiveShadow position={[0, 0.14, 0]}>
        <boxGeometry args={[beam, 0.28, len * 0.7]} />
        <WoodMat color={color} />
      </mesh>
      <mesh castShadow position={[0, 0.18, len * 0.4]}>
        <boxGeometry args={[beam * 0.72, 0.24, len * 0.28]} />
        <WoodMat color={color} />
      </mesh>
      <mesh castShadow position={[0, 0.24, len * 0.54]}>
        <boxGeometry args={[beam * 0.36, 0.16, len * 0.14]} />
        <WoodMat color="#c4a06a" />
      </mesh>
      <mesh castShadow position={[0, 0.12, -len * 0.4]}>
        <boxGeometry args={[beam * 0.86, 0.22, len * 0.18]} />
        <WoodMat color="#3a2818" />
      </mesh>
      <mesh position={[0, 0.32, -0.02]}>
        <boxGeometry args={[beam * 1.08, 0.07, len * 0.78]} />
        <WoodMat color="#e7d3b0" />
      </mesh>
      <mesh castShadow position={[0, 0.48, -len * 0.08]}>
        <boxGeometry args={[beam * 0.7, 0.28, len * 0.28]} />
        <WoodMat color="#6a4030" />
      </mesh>
      <mesh position={[0, 0.66, -len * 0.08]}>
        <boxGeometry args={[beam * 0.78, 0.08, len * 0.32]} />
        <meshStandardMaterial color="#c45532" roughness={0.7} />
      </mesh>
    </group>
  );
}

function ShipSail({
  accent,
  y,
  h,
  w,
}: {
  accent: string;
  y: number;
  h: number;
  w: number;
}) {
  return (
    <group>
      <mesh castShadow position={[0, y, 0]}>
        <cylinderGeometry args={[0.035, 0.05, h * 2.1, 6]} />
        <WoodMat color="#5a4030" />
      </mesh>
      <mesh position={[0, y + h * 0.2, 0]}>
        <planeGeometry args={[w, h]} />
        <meshStandardMaterial color="#f7f1e4" side={THREE.DoubleSide} roughness={0.8} />
      </mesh>
      <mesh position={[0, y + h * 0.55, 0.02]}>
        <planeGeometry args={[w * 0.92, h * 0.42]} />
        <meshStandardMaterial color="#f3e2c4" side={THREE.DoubleSide} roughness={0.8} />
      </mesh>
      <mesh position={[w * 0.28, y + h * 0.35, 0.03]}>
        <boxGeometry args={[w * 0.18, h * 0.55, 0.02]} />
        <meshStandardMaterial color={accent} />
      </mesh>
    </group>
  );
}

function BoatMesh({
  accent,
  kind,
  bob,
  cargo = 0,
}: {
  accent: string;
  kind: "fishing_boat" | "transport" | "war_galley";
  bob: number;
  cargo?: number;
}) {
  if (kind === "transport") {
    return (
      <group position={[0, 0.08 + bob * 0.35, 0]}>
        <ShipHull len={3.6} beam={1.15} color="#4a3830" />
        <mesh castShadow position={[0, 0.55, -0.15]}>
          <boxGeometry args={[0.9, 0.32, 1.5]} />
          <WoodMat color="#5a4838" />
        </mesh>
        {[-0.7, 0, 0.7].map((z, i) => (
          <group key={i}>
            <mesh castShadow position={[0.62, 0.42, z]} rotation={[0, 0, Math.PI / 2]}>
              <cylinderGeometry args={[0.06, 0.08, 0.45, 6]} />
              <meshStandardMaterial color="#3a3a40" metalness={0.75} roughness={0.3} />
            </mesh>
            <mesh castShadow position={[-0.62, 0.42, z]} rotation={[0, 0, Math.PI / 2]}>
              <cylinderGeometry args={[0.06, 0.08, 0.45, 6]} />
              <meshStandardMaterial color="#3a3a40" metalness={0.75} roughness={0.3} />
            </mesh>
          </group>
        ))}
        <ShipSail accent={accent} y={1.15} h={0.7} w={0.55} />
        {cargo > 0 && (
          <mesh position={[0, 0.55, 0.55]}>
            <boxGeometry args={[0.45, 0.18, 0.35]} />
            <meshStandardMaterial color="#e8dcc0" />
          </mesh>
        )}
      </group>
    );
  }
  const len = kind === "war_galley" ? 2.35 : 1.85;
  const beam = kind === "war_galley" ? 0.72 : 0.58;
  const col = kind === "war_galley" ? "#5a4030" : "#8a6244";
  return (
    <group position={[0, 0.06 + bob * 0.4, 0]}>
      <ShipHull len={len} beam={beam} color={col} />
      <ShipSail accent={accent} y={1.05} h={kind === "war_galley" ? 0.95 : 0.72} w={kind === "war_galley" ? 0.72 : 0.55} />
      {kind === "war_galley" ? (
        <mesh castShadow position={[0.28, 0.4, 0.35]} rotation={[0.4, 0, 0.6]}>
          <boxGeometry args={[0.06, 0.06, 0.7]} />
          <meshStandardMaterial color="#c5ccd4" metalness={0.65} roughness={0.3} />
        </mesh>
      ) : null}
    </group>
  );
}


function ResourceHitPad({ r = 1.1, h = 1.6 }: { r?: number; h?: number }) {
  // Invisible fat collider so trees / piles are easy to click for gather orders
  return (
    <mesh position={[0, h * 0.45, 0]} userData={{ resourceHit: true }}>
      <cylinderGeometry args={[r, r, h, 10]} />
      <meshBasicMaterial transparent opacity={0} depthWrite={false} />
    </mesh>
  );
}

function ResourceMesh({
  kind,
  farmTex,
}: {
  kind: "wood" | "food" | "gold" | "stone" | "fish";
  farmTex: THREE.Texture;
}) {
  if (kind === "wood") {
    return <ResourceHitPad r={1.6} h={3.2} />;
  }
  if (kind === "gold") {
    return (
      <group>
        <ResourceHitPad r={1.0} h={1.2} />
        <mesh castShadow position={[0, 0.25, 0]}>
          <dodecahedronGeometry args={[0.45, 0]} />
          <meshStandardMaterial color="#e8c040" metalness={0.75} roughness={0.25} />
        </mesh>
        <mesh castShadow position={[0.35, 0.15, 0.2]}>
          <dodecahedronGeometry args={[0.25, 0]} />
          <meshStandardMaterial color="#d4a820" metalness={0.7} roughness={0.3} />
        </mesh>
      </group>
    );
  }
  if (kind === "stone") {
    return (
      <group>
        <ResourceHitPad r={1.0} h={1.2} />
        <mesh castShadow position={[0, 0.22, 0]}>
          <dodecahedronGeometry args={[0.5, 0]} />
          <meshStandardMaterial color="#9a9588" roughness={0.9} metalness={0.05} />
        </mesh>
        <mesh castShadow position={[-0.3, 0.12, 0.25]}>
          <dodecahedronGeometry args={[0.28, 0]} />
          <meshStandardMaterial color="#7a7568" roughness={0.92} />
        </mesh>
      </group>
    );
  }
  if (kind === "fish") {
    return (
      <group>
        <ResourceHitPad r={0.9} h={0.8} />
        <mesh position={[0, 0.12, 0]} rotation={[0, 0.4, 0.2]}>
          <capsuleGeometry args={[0.08, 0.35, 4, 8]} />
          <meshStandardMaterial color="#6a9aba" roughness={0.4} metalness={0.2} />
        </mesh>
        <mesh position={[0.2, 0.1, 0.15]} rotation={[0, -0.5, 0]}>
          <capsuleGeometry args={[0.06, 0.25, 4, 6]} />
          <meshStandardMaterial color="#5a8aaa" roughness={0.4} />
        </mesh>
      </group>
    );
  }
  return (
    <group>
      <ResourceHitPad r={1.1} h={1.0} />
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.03, 0]} receiveShadow>
        <planeGeometry args={[1.6, 1.6]} />
        <meshStandardMaterial map={farmTex} roughness={0.95} metalness={0.02} />
      </mesh>
      <mesh castShadow position={[0, 0.28, 0]}>
        <sphereGeometry args={[0.32, 10, 8]} />
        <meshStandardMaterial color="#3d8a40" roughness={0.7} metalness={0.05} />
      </mesh>
      <mesh castShadow position={[0.25, 0.22, 0.2]}>
        <sphereGeometry args={[0.2, 8, 6]} />
        <meshStandardMaterial color="#2f6b3a" roughness={0.75} />
      </mesh>
    </group>
  );
}

function ChronoMesh({ tier }: { tier?: string }) {
  const color =
    tier === "A"
      ? "#ffd700"
      : tier === "B"
        ? "#c0c0ff"
        : tier === "C"
          ? "#7ec8e3"
          : "#a8e6a0";
  const ref = useRef<THREE.Mesh>(null);
  useFrame(({ clock }) => {
    if (ref.current) {
      ref.current.rotation.y = clock.elapsedTime * 1.2;
      ref.current.position.y = 0.7 + Math.sin(clock.elapsedTime * 2.5) * 0.12;
    }
  });
  return (
    <group>
      <mesh ref={ref} castShadow>
        <octahedronGeometry args={[0.45, 0]} />
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={0.65}
          metalness={0.85}
          roughness={0.2}
          envMapIntensity={1.4}
        />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.04, 0]}>
        <ringGeometry args={[0.5, 0.75, 32]} />
        <meshBasicMaterial color={color} transparent opacity={0.55} />
      </mesh>
      <pointLight color={color} intensity={0.55} distance={6} decay={2} position={[0, 1.2, 0]} />
    </group>
  );
}

function InstancedForest({
  trees,
  seed,
  biome,
}: {
  trees: Array<{ x: number; z: number; s: number; hue: number }>;
  seed: number;
  biome?: string | null;
}) {
  const trunk = useRef<THREE.InstancedMesh>(null);
  const canopy = useRef<THREE.InstancedMesh>(null);
  const third = useRef<THREE.InstancedMesh>(null);
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const color = useMemo(() => new THREE.Color(), []);
  const leafGeo = useMemo(() => new THREE.PlaneGeometry(2.05, 3.8), []);
  const leafMat = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        map: treeSpriteTexture(),
        transparent: true,
        alphaTest: 0.35,
        roughness: 0.86,
        side: THREE.DoubleSide,
        depthWrite: true,
      }),
    []
  );
  useLayoutEffect(() => {
    const style = terrainStyle(biome);
    trees.forEach((t, i) => {
      const y = terrainHeight(t.x, t.z, seed, biome);
      const s = t.s * (style === "dry_arabia" ? 0.9 : 1.25);
      dummy.position.set(t.x, y + 1.9 * s, t.z);
      dummy.scale.set(s * 0.92, s * (1.05 + (t.hue % 0.35)), s * 0.92);
      dummy.rotation.set(0, t.hue * 6.2, 0);
      dummy.updateMatrix();
      trunk.current?.setMatrixAt(i, dummy.matrix);
      dummy.rotation.set(0, t.hue * 6.2 + Math.PI / 3, 0);
      dummy.updateMatrix();
      canopy.current?.setMatrixAt(i, dummy.matrix);
      dummy.rotation.set(0, t.hue * 6.2 + (Math.PI * 2) / 3, 0);
      dummy.updateMatrix();
      third.current?.setMatrixAt(i, dummy.matrix);
      const tint = i % 4 === 0 ? "#d7e4cc" : i % 4 === 1 ? "#f4f7ef" : i % 4 === 2 ? "#b7cba6" : "#e4efe0";
      color.set(tint);
      canopy.current?.setColorAt(i, color);
      trunk.current?.setColorAt(i, color);
      third.current?.setColorAt(i, color);
    });
    for (const mesh of [trunk.current, canopy.current, third.current]) {
      if (!mesh) continue;
      mesh.count = trees.length;
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    }
  }, [trees, seed, biome, dummy, color]);
  if (!trees.length) return null;
  return (
    <group raycast={() => null}>
      <instancedMesh ref={trunk} args={[leafGeo, leafMat, trees.length]} />
      <instancedMesh ref={canopy} args={[leafGeo, leafMat, trees.length]} />
      <instancedMesh ref={third} args={[leafGeo, leafMat, trees.length]} />
    </group>
  );
}

function LandmarkSites({ seed, biome }: { seed: number; biome?: string | null }) {
  const sites = useMemo(() => terrainSites(seed, biome), [seed, biome]);
  return (
    <group raycast={() => null}>
      {sites.map((s) => {
        const y = terrainHeight(s.x, s.z, seed, biome);
        if (s.kind === "sacred") {
          return (
            <group key="sacred" position={[s.x, y, s.z]}>
              <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.12, 0]}>
                <ringGeometry args={[1.35, 1.75, 18]} />
                <meshStandardMaterial color="#e4d08a" roughness={0.6} />
              </mesh>
              <mesh position={[0, 0.85, 0]}>
                <boxGeometry args={[0.38, 1.5, 0.38]} />
                <meshStandardMaterial color="#d9d2c2" roughness={0.7} />
              </mesh>
            </group>
          );
        }
        return (
          <group key={`trade-${s.x}-${s.z}`} position={[s.x, y, s.z]}>
            <mesh position={[0, 0.45, 0]} castShadow receiveShadow>
              <boxGeometry args={[1.45, 0.85, 1.15]} />
              <meshStandardMaterial map={woodTex()} color="#8a6240" roughness={0.88} />
            </mesh>
            <GableRoof w={1.75} d={1.45} rise={0.48} y={0.88} color="#b84532" />
          </group>
        );
      })}
    </group>
  );
}

/**
 * Soft fog-of-war: bilinear DataTexture so cell edges feather instead of hard tiles.
 */

/** Continuous lake/river surface (DataTexture) — not Minecraft water tiles. */
function WaterLayer({ water, seed }: { water: WaterPayload; seed: number }) {
  const { tex, size } = useMemo(() => {
    const cell = water?.cell || 2;
    const mapHalf = water?.mapHalf || 32;
    const n = Math.max(8, Math.ceil((mapHalf * 2) / cell));
    const data = new Uint8Array(n * n);
    for (const key of water?.cells || []) {
      const [ix, iz] = key.split(",").map(Number);
      if (ix < 0 || iz < 0 || ix >= n || iz >= n) continue;
      data[iz * n + ix] = 255;
    }
    // 1-pass blur so shores feather instead of square steps
    const blur = new Uint8Array(n * n);
    for (let z = 0; z < n; z++) {
      for (let x = 0; x < n; x++) {
        let s = 0;
        let c = 0;
        for (let dz = -1; dz <= 1; dz++) {
          for (let dx = -1; dx <= 1; dx++) {
            const xx = x + dx;
            const zz = z + dz;
            if (xx < 0 || zz < 0 || xx >= n || zz >= n) continue;
            s += data[zz * n + xx];
            c++;
          }
        }
        blur[z * n + x] = Math.min(255, Math.round(s / Math.max(1, c)));
      }
    }
    const t = new THREE.DataTexture(blur, n, n, THREE.RedFormat);
    t.magFilter = THREE.LinearFilter;
    t.minFilter = THREE.LinearFilter;
    t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
    t.needsUpdate = true;
    return { tex: t, size: mapHalf * 2 };
  }, [water, seed]);

  const mat = useMemo(
    () =>
      new THREE.ShaderMaterial({
        transparent: true,
        depthWrite: false,
        uniforms: {
          uWater: { value: tex },
          uDeep: { value: new THREE.Color("#143e78") },
          uMid: { value: new THREE.Color("#2f74b8") },
          uShallow: { value: new THREE.Color("#8ec8dc") },
          uTime: { value: 0 },
        },
        vertexShader: /* glsl */ `
          varying vec2 vUv;
          varying vec3 vWorld;
          void main() {
            vUv = uv;
            vec4 w = modelMatrix * vec4(position, 1.0);
            vWorld = w.xyz;
            gl_Position = projectionMatrix * viewMatrix * w;
          }
        `,
        fragmentShader: /* glsl */ `
          uniform sampler2D uWater;
          uniform vec3 uDeep;
          uniform vec3 uMid;
          uniform vec3 uShallow;
          uniform float uTime;
          varying vec2 vUv;
          varying vec3 vWorld;
          void main() {
            float d = texture2D(uWater, vUv).r;
            if (d < 0.08) discard;
            float along = vWorld.x * 0.22 + vWorld.z * 0.55;
            float flow = sin(along - uTime * 0.55);
            float rip = sin(vWorld.x * 1.15 - vWorld.z * 0.4 + uTime * 0.85);
            float depth = smoothstep(0.18, 0.9, d);
            vec3 col = mix(uShallow, uDeep, depth);
            col = mix(col, uMid, 0.22 + flow * 0.08);
            col += vec3(0.05, 0.08, 0.1) * rip * (1.0 - depth);
            float shore = smoothstep(0.08, 0.2, d) * (1.0 - smoothstep(0.2, 0.4, d));
            float foam = shore * (0.55 + 0.45 * sin(along * 3.0 - uTime * 1.2));
            col = mix(col, vec3(0.9, 0.95, 0.93), foam);
            float alpha = smoothstep(0.08, 0.32, d) * 0.94;
            gl_FragColor = vec4(col, alpha);
          }
        `,
      }),
    [tex]
  );

  useFrame(({ clock }) => {
    mat.uniforms.uTime.value = clock.elapsedTime;
  });

  if (!water?.cells?.length) return null;
  return (
    <mesh
      rotation={[-Math.PI / 2, 0, 0]}
      position={[0, WATERLINE, 0]}
      material={mat}
      raycast={() => null}
    >
      <planeGeometry args={[size, size]} />
    </mesh>
  );
}


function BeachSandLayer({ beaches, water }: { beaches?: string[]; water: WaterPayload }) {
  const { tex, size } = useMemo(() => {
    const cell = water?.cell || 2;
    const mapHalf = water?.mapHalf || 32;
    const n = Math.max(8, Math.ceil((mapHalf * 2) / cell));
    const data = new Uint8Array(n * n);
    for (const key of beaches || []) {
      const [ix, iz] = key.split(",").map(Number);
      if (ix < 0 || iz < 0 || ix >= n || iz >= n) continue;
      data[iz * n + ix] = 255;
    }
    const t = new THREE.DataTexture(data, n, n, THREE.RedFormat);
    t.magFilter = THREE.NearestFilter;
    t.minFilter = THREE.NearestFilter;
    t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
    t.needsUpdate = true;
    return { tex: t, size: mapHalf * 2 };
  }, [beaches, water]);

  const mat = useMemo(() => {
    return new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      uniforms: {
        uMask: { value: tex },
        uColor: { value: new THREE.Color("#d8c49a") },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D uMask;
        uniform vec3 uColor;
        varying vec2 vUv;
        void main() {
          float m = texture2D(uMask, vUv).r;
          if (m < 0.15) discard;
          gl_FragColor = vec4(uColor, 0.92 * m);
        }
      `,
    });
  }, [tex]);

  if (!(beaches && beaches.length)) return null;
  return (
    <mesh
      rotation={[-Math.PI / 2, 0, 0]}
      position={[0, 0.03, 0]}
      material={mat}
      raycast={() => null}
    >
      <planeGeometry args={[size, size]} />
    </mesh>
  );
}

function SoftFogLayer({
  fog,
  quality,
}: {
  fog: FogPayload;
  quality: GraphicsQuality;
}) {
  const meshRef = useRef<THREE.Mesh>(null);
  const { tex, mat } = useMemo(() => {
    const half = fog?.mapHalf || 32;
    const cell = fog?.cell || 2;
    const n = Math.ceil((half * 2) / cell);
    const data = new Uint8Array(n * n);
    const t = new THREE.DataTexture(data, n, n, THREE.RedFormat);
    t.magFilter = THREE.LinearFilter;
    t.minFilter = THREE.LinearFilter;
    t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
    t.colorSpace = THREE.NoColorSpace;
    t.needsUpdate = true;
    // Density 0 = clear vision, 1 = densest fog. Cap alpha so grass always shows.
    const m = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      uniforms: {
        uFog: { value: t },
        uTint: { value: new THREE.Color("#243a28") },
        uDeep: { value: new THREE.Color("#121c14") },
      },
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        uniform sampler2D uFog;
        uniform vec3 uTint;
        uniform vec3 uDeep;
        varying vec2 vUv;
        void main() {
          float d = texture2D(uFog, vUv).r;
          if (d < 0.02) discard;
          // Terrain always readable — soft darken, never empty black canvas
          float alpha = mix(0.18, 0.48, smoothstep(0.05, 1.0, d));
          vec3 col = mix(uTint, uDeep, smoothstep(0.15, 1.0, d));
          gl_FragColor = vec4(col, alpha);
        }
      `,
    });
    return { tex: t, mat: m, n, half };
  }, [fog?.cell, fog?.mapHalf]);

  useEffect(() => {
    if (!fog) {
      const data = tex.image.data as Uint8Array;
      data.fill(0);
      tex.needsUpdate = true;
      return;
    }
    const cell = fog.cell || 2;
    const half = fog.mapHalf || 32;
    const n = Math.ceil((half * 2) / cell);
    const explored = new Set(fog.explored || []);
    const visible = new Set(fog.visible || []);
    const data = tex.image.data as Uint8Array;
    // 0 = visible (clear), ~90 = explored dim, ~170 = unexplored (dark green, not black)
    const exploredV = quality === "high" ? 70 : 80;
    const unexploredV = quality === "high" ? 140 : 150;
    for (let iz = 0; iz < n; iz++) {
      for (let ix = 0; ix < n; ix++) {
        const key = `${ix},${iz}`;
        let v = unexploredV;
        if (visible.has(key)) v = 0;
        else if (explored.has(key)) v = exploredV;
        data[iz * n + ix] = v;
      }
    }
    tex.needsUpdate = true;
  }, [fog, quality, tex]);

  if (!fog) return null;
  const half = fog.mapHalf || 32;
  const size = half * 2;

  return (
    <mesh
      ref={meshRef}
      rotation={[-Math.PI / 2, 0, 0]}
      position={[0, 0.1, 0]}
      material={mat}
      raycast={() => null}
    >
      <planeGeometry args={[size, size, quality === "high" ? 64 : 32, quality === "high" ? 64 : 32]} />
    </mesh>
  );
}

function barHeight(kind: string): number {
  if (kind === "town_hall" || kind === "keep") return 4.4;
  if (kind === "church" || kind === "university" || kind === "barracks" || kind === "stable") return 3.5;
  if (
    kind === "house" ||
    kind === "farm" ||
    kind === "mill" ||
    kind === "market" ||
    kind === "dock" ||
    kind === "lumber_camp" ||
    kind === "mining_camp" ||
    kind === "archery_range" ||
    kind === "blacksmith" ||
    kind === "watch_tower" ||
    kind === "siege_workshop" ||
    kind === "lighthouse"
  )
    return 3.1;
  if (kind === "wall" || kind === "fortified_wall" || kind === "palisade" || kind === "gate") return 2.4;
  if (kind === "transport" || kind === "war_galley") return 2.8;
  if (kind === "fishing_boat") return 2.3;
  if (kind === "war_elephant" || kind === "knight" || kind === "scout_cav" || kind === "camel_rider") return 2.7;
  if (kind === "worker" || kind === "healer" || kind === "geologist" || kind === "explorer") return 2.35;
  return 2.35;
}

function glideSpeed(kind: string): number {
  if (kind === "worker" || kind === "geologist" || kind === "healer") return 2.4;
  if (kind === "scout_cav" || kind === "explorer") return 4.2;
  if (kind === "knight" || kind === "camel_rider") return 3.8;
  if (kind === "war_elephant") return 2.3;
  if (kind === "fishing_boat" || kind === "war_galley" || kind === "transport") return 3.1;
  if (kind === "deer" || kind === "lion" || kind === "coyote" || kind === "gorilla") return 2.6;
  return 2.7;
}

/** Mobile kinds that must lerp — hard snaps look like teleports between polls. */
const SMOOTH_POS_KINDS = new Set([
  "worker",
  "militia",
  "spearman",
  "man_at_arms",
  "archer",
  "skirmisher",
  "crossbowman",
  "hand_cannon",
  "scout_cav",
  "knight",
  "camel_rider",
  "war_elephant",
  "ram",
  "mangonel",
  "scorpion",
  "trebuchet",
  "fishing_boat",
  "transport",
  "war_galley",
  "geologist",
  "healer",
  "explorer",
  "trade_cart",
  "sheep",
  "chicken",
  "cow",
  "goat",
  "duck",
  "deer",
  "lion",
  "coyote",
  "gorilla",
  "beach_kid",
]);

const ON_WATER = new Set(["fishing_boat", "transport", "war_galley", "fish"]);
const ON_PAD = new Set([
  "town_hall",
  "barracks",
  "house",
  "mill",
  "lumber_camp",
  "mining_camp",
  "archery_range",
  "stable",
  "blacksmith",
  "university",
  "church",
  "market",
  "dock",
  "lighthouse",
  "oil_well",
  "watch_tower",
  "keep",
  "siege_workshop",
  "farm",
  "palisade",
  "wall",
  "fortified_wall",
  "gate",
]);

function groundY(kind: string, x: number, z: number): { y: number; sink: number } {
  if (ON_WATER.has(kind)) return { y: WATERLINE + (kind === "fish" ? 0.02 : 0.06), sink: 0 };
  const surface = terrainHeight(x, z);
  const big = kind === "town_hall" || kind === "keep";
  const hx = ON_PAD.has(kind) ? (big ? 2.3 : kind === "farm" ? 1.6 : 1.35) : 0.55;
  const hz = ON_PAD.has(kind) ? (big ? 1.9 : hx) : 0.55;
  let y = surface;
  for (let ix = 0; ix < 5; ix++) {
    for (let iz = 0; iz < 5; iz++) {
      const sx = (ix / 4 - 0.5) * 2 * hx;
      const sz = (iz / 4 - 0.5) * 2 * hz;
      y = Math.max(y, terrainHeight(x + sx, z + sz));
    }
  }
  y += ON_PAD.has(kind) ? 0.55 : 0.12;
  if (!ON_PAD.has(kind)) return { y, sink: 0 };
  return { y, sink: Math.max(0, y - surface) };
}

function plantOnGround(g: THREE.Group, kind: string, x: number, z: number, yaw: number) {
  const planted = groundY(kind, x, z);
  g.position.set(x, planted.y, z);
  const plinth = g.children.find((c) => c.name === "plinth") as THREE.Mesh | undefined;
  if (plinth) {
    plinth.visible = planted.sink > 0.05;
    plinth.scale.y = Math.max(0.08, planted.sink);
    plinth.position.y = -planted.sink / 2;
  }
  g.rotation.y = yaw;
}

function EntityView({
  e,
  selected,
  onSelect,
  onContext,
  farmTex,
  fortMask = 0,
}: {
  e: PublicEntity;
  selected: boolean;
  onSelect: (id: string, shift: boolean, alt?: boolean) => void;
  onContext: (id: string) => void;
  farmTex: THREE.Texture;
  fortMask?: number;
}) {
  const accent = e.team ? TEAM_ACCENT[e.team] : "#888";
  const banner = e.team ? TEAM_BANNER[e.team] : "#888";
  const moving =
    e.order === "move" ||
    e.order === "gather" ||
    e.order === "attack" ||
    e.order === "capture" ||
    e.order === "return" ||
    e.order === "build" ||
    e.order === "attack_move" ||
    e.order === "heal" ||
    e.order === "browse";
  const bobRef = useRef(0);
  const [bob, setBob] = useState(0);
  const gaitRef = useRef<Gait>({ phase: 0, amp: 0 });
  const groupRef = useRef<THREE.Group>(null);
  const dispRef = useRef({
    x: e.x,
    z: e.z,
    sx: e.x,
    sz: e.z,
    st: 0,
    vx: 0,
    vz: 0,
    facing: e.facing || 0,
    primed: false,
  });
  useLayoutEffect(() => {
    const g = groupRef.current;
    if (!g) return;
    plantOnGround(g, e.kind, e.x, e.z, e.facing || 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useFrame((_, dt) => {
    const stepDt = Math.min(dt, 0.05);
    if (moving) {
      bobRef.current += stepDt * 8;
      const next = Math.abs(Math.sin(bobRef.current)) * 0.07;
      if (Math.abs(next - bob) > 0.02) setBob(next);
    } else if (bob !== 0) setBob(0);

    const g = groupRef.current;
    if (!g) return;
    if (!SMOOTH_POS_KINDS.has(e.kind)) {
      plantOnGround(g, e.kind, e.x, e.z, 0);
      return;
    }
    const d = dispRef.current;
    const now = performance.now() / 1000;
    if (!d.primed) {
      d.x = e.x;
      d.z = e.z;
      d.sx = e.x;
      d.sz = e.z;
      d.st = now;
      d.facing = e.facing || 0;
      d.primed = true;
      plantOnGround(g, e.kind, e.x, e.z, d.facing);
      return;
    }
    const hasAim = moving && typeof e.ox === "number" && typeof e.oz === "number";
    if (d.sx !== e.x || d.sz !== e.z) {
      const dtS = Math.max(0.05, now - (d.st || now));
      let vx = (e.x - d.sx) / dtS;
      let vz = (e.z - d.sz) / dtS;
      const sp = Math.hypot(vx, vz) || 1;
      const cap = glideSpeed(e.kind) * 1.25;
      if (sp > cap) {
        vx *= cap / sp;
        vz *= cap / sp;
      }
      d.vx = vx;
      d.vz = vz;
      d.sx = e.x;
      d.sz = e.z;
      d.st = now;
    }
    const age = moving && !hasAim ? Math.min(1.6, Math.max(0, now - d.st)) : 0;
    const speedCap = glideSpeed(e.kind);
    const goalX = hasAim ? (e.ox as number) : e.x + (moving ? d.vx * age : 0);
    const goalZ = hasAim ? (e.oz as number) : e.z + (moving ? d.vz * age : 0);
    const dx = goalX - d.x;
    const dz = goalZ - d.z;
    const dist = Math.hypot(dx, dz);
    const beforeX = d.x;
    const beforeZ = d.z;
    const serverJump = Math.hypot(e.x - d.x, e.z - d.z);
    if (!hasAim && (dist > 12 || serverJump > 14)) {
      d.x = e.x;
      d.z = e.z;
      d.vx = 0;
      d.vz = 0;
    } else if (dist > 0.004) {
      const step = Math.min(dist, speedCap * stepDt);
      d.x += (dx / dist) * step;
      d.z += (dz / dist) * step;
      d.x = Math.max(-MAP_HALF, Math.min(MAP_HALF, d.x));
      d.z = Math.max(-MAP_HALF, Math.min(MAP_HALF, d.z));
      const face = Math.atan2(dx, dz);
      const turn = Math.atan2(Math.sin(face - d.facing), Math.cos(face - d.facing));
      d.facing += turn * Math.min(1, stepDt * 8);
    } else if (typeof e.facing === "number") {
      const turn = Math.atan2(Math.sin(e.facing - d.facing), Math.cos(e.facing - d.facing));
      d.facing += turn * Math.min(1, stepDt * 6);
    }
    const stepped = Math.hypot(d.x - beforeX, d.z - beforeZ);
    const gait = gaitRef.current;
    if (stepped > 0.004) {
      gait.phase += stepDt * 9;
      gait.amp = Math.min(1, gait.amp + stepDt * 8);
    } else {
      gait.amp = Math.max(0, gait.amp - stepDt * 6);
    }
    plantOnGround(g, e.kind, d.x, d.z, d.facing);
  });

  const fortTier =
    e.kind === "palisade" ? "wood" : e.kind === "fortified_wall" ? "fort" : "stone";

  let body: ReactNode = null;
  if (e.kind === "town_hall") body = <TownHallMesh accent={banner} />;
  else if (e.kind === "barracks") body = <BarracksMesh accent={banner} />;
  else if (e.kind === "house") body = <HouseMesh accent={banner} />;
  else if (e.kind === "mill" || e.kind === "granary") body = <CampMesh accent={banner} tint={e.kind === "granary" ? "#c4a15a" : "#8a7048"} />;
  else if (e.kind === "storage_pit") body = <CampMesh accent={banner} tint="#6a6458" />;
  else if (e.kind === "wonder") body = <TownHallMesh accent="#f0d78c" />;
  else if (e.kind === "ruin") body = <TowerMesh accent="#9a9080" />;
  else if (e.kind === "artifact") body = <ChronoMesh tier="B" />;
  else if (e.kind === "lumber_camp") body = <CampMesh accent={banner} tint="#5a4030" />;
  else if (e.kind === "mining_camp") body = <CampMesh accent={banner} tint="#707068" />;
  else if (e.kind === "archery_range") body = <ArcheryMesh accent={banner} />;
  else if (e.kind === "stable") body = <StableMesh accent={banner} />;
  else if (e.kind === "blacksmith") body = <BlacksmithMesh accent={banner} />;
  else if (e.kind === "university") body = <UniversityMesh accent={banner} />;
  else if (e.kind === "church") body = <ChurchMesh accent={banner} />;
  else if (e.kind === "market") body = <MarketMesh accent={banner} />;
  else if (e.kind === "dock") body = <DockMesh accent={banner} />;
  else if (e.kind === "lighthouse") body = <LighthouseMesh />;
  else if (e.kind === "oil_well" || e.kind === "oil_seep" || e.kind === "gas_seep")
    body = <OilWellMesh kind={e.kind} accent={banner} />;
  else if (e.kind === "beach_kid" || e.kind === "geologist")
    body = <ScenicPersonMesh kind={e.kind} gait={gaitRef} />;
  else if (e.kind === "watch_tower") body = <TowerMesh accent={banner} />;
  else if (e.kind === "palisade" || e.kind === "wall" || e.kind === "fortified_wall")
    body = <WallMesh accent={banner} mask={fortMask} tier={fortTier} />;
  else if (e.kind === "gate")
    body = (
      <GateMesh
        accent={banner}
        mask={fortMask}
        open={e.gateState === "open"}
      />
    );
  else if (e.kind === "keep") body = <KeepMesh accent={banner} />;
  else if (e.kind === "siege_workshop") body = <SiegeMesh accent={banner} />;
  else if (e.kind === "farm") body = <FarmMesh farmTex={farmTex} />;
  else if (e.kind === "trade_cart")
    body = (
      <group>
        <mesh castShadow position={[0, 0.28, 0]}>
          <boxGeometry args={[0.72, 0.28, 0.95]} />
          <WoodMat color="#6a5038" />
        </mesh>
        <mesh castShadow position={[-0.28, 0.16, 0.28]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.16, 0.16, 0.08, 10]} />
          <meshStandardMaterial color="#3a2a1c" />
        </mesh>
        <mesh castShadow position={[0.28, 0.16, 0.28]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.16, 0.16, 0.08, 10]} />
          <meshStandardMaterial color="#3a2a1c" />
        </mesh>
        <mesh castShadow position={[-0.28, 0.16, -0.28]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.16, 0.16, 0.08, 10]} />
          <meshStandardMaterial color="#3a2a1c" />
        </mesh>
        <mesh castShadow position={[0.28, 0.16, -0.28]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.16, 0.16, 0.08, 10]} />
          <meshStandardMaterial color="#3a2a1c" />
        </mesh>
        <mesh castShadow position={[0, 0.62, -0.2]}>
          <boxGeometry args={[0.22, 0.32, 0.16]} />
          <meshStandardMaterial color={banner} />
        </mesh>
        <mesh position={[0, 0.86, -0.18]}>
          <sphereGeometry args={[0.1, 8, 6]} />
          <meshStandardMaterial color="#e7b48a" />
        </mesh>
      </group>
    );
  else if (e.kind === "worker")
    body = (
      <WorkerMesh accent={banner} bob={bob} gait={gaitRef} workProgress={e.workProgress} />
    );
  else if (e.kind === "healer") body = <Person role="healer" accent={banner} bob={bob} gait={gaitRef} />;
  else if (e.kind === "explorer") body = <Person role="explorer" accent={banner} bob={bob} gait={gaitRef} />;
  else if (e.kind === "militia") body = <Person role="militia" accent={banner} bob={bob} gait={gaitRef} />;
  else if (e.kind === "spearman") body = <SpearmanMesh accent={banner} bob={bob} gait={gaitRef} />;
  else if (e.kind === "man_at_arms") body = <ManAtArmsMesh accent={banner} bob={bob} gait={gaitRef} />;
  else if (e.kind === "archer") body = <ArcherMesh accent={banner} bob={bob} gait={gaitRef} />;
  else if (e.kind === "skirmisher")
    body = <ArcherMesh accent={banner} bob={bob} gait={gaitRef} role="skirmisher" />;
  else if (e.kind === "crossbowman") body = <CrossbowMesh accent={banner} bob={bob} gait={gaitRef} />;
  else if (e.kind === "hand_cannon") body = <HandCannonMesh accent={banner} bob={bob} gait={gaitRef} />;
  else if (e.kind === "scout_cav") body = <CavalryMesh accent={banner} bob={bob} gait={gaitRef} />;
  else if (e.kind === "knight") body = <CavalryMesh accent={banner} bob={bob} knight gait={gaitRef} />;
  else if (e.kind === "camel_rider") body = <CamelMesh accent={banner} bob={bob} gait={gaitRef} />;
  else if (e.kind === "war_elephant") body = <WarElephant accent={banner} bob={bob} gait={gaitRef} />;
  else if (e.kind === "ram") body = <RamMesh accent={banner} bob={bob} />;
  else if (e.kind === "mangonel") body = <MangonelMesh accent={banner} bob={bob} />;
  else if (e.kind === "scorpion") body = <ScorpionMesh accent={banner} bob={bob} />;
  else if (e.kind === "trebuchet") body = <TrebuchetMesh accent={banner} bob={bob} />;
  else if (e.kind === "fishing_boat" || e.kind === "transport" || e.kind === "war_galley")
    body = (
      <BoatMesh
        accent={banner}
        kind={e.kind}
        bob={bob}
        cargo={e.cargo || 0}
      />
    );
  else if (e.kind === "wood") body = <ResourceMesh kind="wood" farmTex={farmTex} />;
  else if (e.kind === "food") body = <ResourceMesh kind="food" farmTex={farmTex} />;
  else if (e.kind === "gold") body = <ResourceMesh kind="gold" farmTex={farmTex} />;
  else if (e.kind === "stone") body = <ResourceMesh kind="stone" farmTex={farmTex} />;
  else if (e.kind === "fish") body = <ResourceMesh kind="fish" farmTex={farmTex} />;
  else if (
    e.kind === "sheep" ||
    e.kind === "chicken" ||
    e.kind === "cow" ||
    e.kind === "goat" ||
    e.kind === "duck" ||
    e.kind === "deer" ||
    e.kind === "lion" ||
    e.kind === "coyote" ||
    e.kind === "gorilla"
  )
    body = <AnimalMesh kind={e.kind} gait={gaitRef} />;
  else if (e.kind === "chrono") body = <ChronoMesh tier={e.chronoTier} />;

  return (
    <group
      ref={groupRef}
      onClick={(ev) => {
        ev.stopPropagation();
        onSelect(e.id, ev.shiftKey, ev.altKey);
      }}
      onContextMenu={(ev) => {
        ev.nativeEvent.preventDefault();
      }}
    >
      {UNIT_KINDS.has(e.kind) ? (
        <mesh position={[0, 1.15, 0]}>
          <cylinderGeometry args={[0.62, 0.62, 2.1, 8]} />
          <meshBasicMaterial transparent opacity={0} depthWrite={false} />
        </mesh>
      ) : null}
      <mesh name="plinth" position={[0, -0.04, 0]} visible={false} receiveShadow>
        <cylinderGeometry
          args={[
            e.kind === "farm" ? 1.5 : e.kind === "town_hall" || e.kind === "keep" ? 2.15 : 1.25,
            e.kind === "town_hall" || e.kind === "keep" ? 2.35 : 1.45,
            1,
            10,
          ]}
        />
        <meshStandardMaterial color="#8a7a62" roughness={0.95} />
      </mesh>
      {body}
      {e.constructing &&
      (e.kind === "palisade" || e.kind === "wall" || e.kind === "fortified_wall" || e.kind === "gate") ? (
        <mesh position={[0, 0.62, 0]}>
          <boxGeometry args={[0.96, 0.85, 0.42]} />
          <meshBasicMaterial color="#e7c56a" transparent opacity={0.78} depthWrite={false} />
        </mesh>
      ) : e.constructing ? (
        <mesh position={[0, 1.8, 0]}>
          <boxGeometry args={[0.3, 0.3, 0.3]} />
          <meshStandardMaterial
            color="#f4e8c0"
            emissive="#f4e8c0"
            emissiveIntensity={0.4}
            metalness={0.3}
            roughness={0.4}
          />
        </mesh>
      ) : null}
      {selected && (
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.05, 0]}>
          <ringGeometry args={[0.45, 0.62, 32]} />
          <meshBasicMaterial color="#f4e8c0" transparent opacity={0.95} />
        </mesh>
      )}
      {e.hp > 0 && (WHO[e.kind] || e.maxHp > 1) ? (
        <WhoPlate kind={e.kind} hp={e.hp} maxHp={e.maxHp} y={barHeight(e.kind)} />
      ) : null}
    </group>
  );
}

type PanApi = {
  panTo: (x: number, z: number) => void;
  getPan: () => { x: number; z: number };
  /** World XZ → pixel coords relative to the WebGL canvas (for box-select). */
  worldToClient: (x: number, z: number) => { x: number; y: number } | null;
  /** Screen client coords → ground XZ (for fort wall drag-draw). */
  clientToWorld: (clientX: number, clientY: number) => { x: number; z: number } | null;
};

const PAN_LIMIT = MAP_HALF + 80;

function IsoCamera({
  focus,
  panSpeed = 1,
  panApiRef,
  dragPan,
  orbitNudge,
  edgePan,
}: {
  focus: [number, number, number];
  panSpeed?: number;
  panApiRef: React.MutableRefObject<PanApi | null>;
  dragPan: React.MutableRefObject<{ dx: number; dz: number }>;
  orbitNudge: React.MutableRefObject<{ yaw: number; pitch: number }>;
  edgePan: React.MutableRefObject<{ x: number; z: number }>;
}) {
  const { camera, gl } = useThree();
  const pan = useRef({ x: 0, z: 0 });
  const zoom = useRef(11);
  const orbit = useRef({ yaw: Math.PI / 4, pitch: 0.58 });
  const keys = useRef({ u: false, d: false, l: false, r: false });
  const lastFocus = useRef<[number, number, number] | null>(null);

  // Snap to new Town Center when match / team focus jumps (restart practice)
  useEffect(() => {
    const prev = lastFocus.current;
    if (
      prev &&
      Math.hypot(focus[0] - prev[0], focus[2] - prev[2]) > 6
    ) {
      pan.current = { x: 0, z: 0 };
    }
    lastFocus.current = focus;
  }, [focus]);

  useEffect(() => {
    panApiRef.current = {
      panTo: (x, z) => {
        pan.current.x = Math.max(-PAN_LIMIT, Math.min(PAN_LIMIT, x - focus[0]));
        pan.current.z = Math.max(-PAN_LIMIT, Math.min(PAN_LIMIT, z - focus[2]));
      },
      getPan: () => ({ ...pan.current }),
      worldToClient: (wx, wz) => {
        const v = new THREE.Vector3(wx, terrainHeight(wx, wz) + 0.7, wz);
        v.project(camera);
        if (!Number.isFinite(v.x) || !Number.isFinite(v.y)) return null;
        const canvas = gl.domElement;
        const w = canvas.clientWidth || 1;
        const h = canvas.clientHeight || 1;
        return {
          x: (v.x * 0.5 + 0.5) * w,
          y: (-v.y * 0.5 + 0.5) * h,
        };
      },
      clientToWorld: (clientX, clientY) => {
        const canvas = gl.domElement;
        const rect = canvas.getBoundingClientRect();
        if (rect.width < 1 || rect.height < 1) return null;
        const ndcX = ((clientX - rect.left) / rect.width) * 2 - 1;
        const ndcY = -((clientY - rect.top) / rect.height) * 2 + 1;
        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);
        const ray = raycaster.ray;
        let lo = 0;
        let hi = 280;
        let hit: THREE.Vector3 | null = null;
        for (let i = 0; i < 22; i++) {
          const mid = (lo + hi) * 0.5;
          const p = ray.at(mid, new THREE.Vector3());
          const h = terrainHeight(p.x, p.z);
          if (p.y > h) lo = mid;
          else {
            hi = mid;
            hit = p;
          }
        }
        if (!hit || !Number.isFinite(hit.x) || !Number.isFinite(hit.z)) return null;
        const edge = MAP_HALF - 1;
        return {
          x: Math.max(-edge, Math.min(edge, hit.x)),
          z: Math.max(-edge, Math.min(edge, hit.z)),
        };
      },
    };
  }, [focus, panApiRef, camera, gl]);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      const k = e.key.toLowerCase();
      if (k === "w" || k === "arrowup") keys.current.u = true;
      if (k === "s" || k === "arrowdown") keys.current.d = true;
      if (k === "a" || k === "arrowleft") keys.current.l = true;
      if (k === "d" || k === "arrowright") keys.current.r = true;
    };
    const up = (e: KeyboardEvent) => {
      const k = e.key.toLowerCase();
      if (k === "w" || k === "arrowup") keys.current.u = false;
      if (k === "s" || k === "arrowdown") keys.current.d = false;
      if (k === "a" || k === "arrowleft") keys.current.l = false;
      if (k === "d" || k === "arrowright") keys.current.r = false;
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, []);

  // Scroll zooms toward the ground point under the pointer.
  useEffect(() => {
    const el = gl.domElement;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const ortho = camera as THREE.OrthographicCamera;
      if (!("zoom" in ortho)) return;
      const before = panApiRef.current?.clientToWorld(e.clientX, e.clientY);
      const dir = e.deltaY > 0 ? -1 : 1;
      const next = zoom.current * (dir > 0 ? 1.08 : 0.92);
      zoom.current = Math.max(4, Math.min(40, next));
      ortho.zoom = zoom.current;
      ortho.updateProjectionMatrix();
      if (!before) return;
      const after = panApiRef.current?.clientToWorld(e.clientX, e.clientY);
      if (!after) return;
      pan.current.x = Math.max(-PAN_LIMIT, Math.min(PAN_LIMIT, pan.current.x + (before.x - after.x)));
      pan.current.z = Math.max(-PAN_LIMIT, Math.min(PAN_LIMIT, pan.current.z + (before.z - after.z)));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [gl, camera, panApiRef]);

  useFrame((_, dt) => {
    const speed = 14 * panSpeed * dt;
    const yawNow = orbit.current.yaw;
    const rightX = Math.sin(yawNow);
    const rightZ = -Math.cos(yawNow);
    const upX = -Math.cos(yawNow);
    const upZ = -Math.sin(yawNow);
    let sx = 0;
    let sy = 0;
    if (keys.current.r) sx += 1;
    if (keys.current.l) sx -= 1;
    if (keys.current.u) sy += 1;
    if (keys.current.d) sy -= 1;
    if (edgePan.current.x) sx += edgePan.current.x;
    if (edgePan.current.z) sy += edgePan.current.z;
    const edgeBoost = edgePan.current.x || edgePan.current.z ? 1.4 : 1;
    let dx = dragPan.current.dx;
    let dz = dragPan.current.dz;
    dragPan.current.dx = 0;
    dragPan.current.dz = 0;
    dx += (sx * rightX + sy * upX) * speed * edgeBoost;
    dz += (sx * rightZ + sy * upZ) * speed * edgeBoost;
    pan.current.x = Math.max(-PAN_LIMIT, Math.min(PAN_LIMIT, pan.current.x + dx));
    pan.current.z = Math.max(-PAN_LIMIT, Math.min(PAN_LIMIT, pan.current.z + dz));
    orbit.current.yaw += orbitNudge.current.yaw;
    orbit.current.pitch = Math.max(
      0.42,
      Math.min(1.2, orbit.current.pitch + orbitNudge.current.pitch)
    );
    orbitNudge.current.yaw = 0;
    orbitNudge.current.pitch = 0;
    const [fx, fy, fz] = focus;
    const tx = fx + pan.current.x;
    const tz = fz + pan.current.z;
    const radius = 34;
    const yaw = orbit.current.yaw;
    const pitch = orbit.current.pitch;
    const cp = Math.cos(pitch);
    camera.position.set(
      tx + Math.cos(yaw) * radius * cp,
      fy + Math.sin(pitch) * radius,
      tz + Math.sin(yaw) * radius * cp
    );
    camera.lookAt(tx, fy + terrainHeight(tx, tz) * 0.35, tz);
    if ("zoom" in camera) {
      const ortho = camera as THREE.OrthographicCamera;
      if (Math.abs(ortho.zoom - zoom.current) > 0.001) {
        ortho.zoom = zoom.current;
        ortho.updateProjectionMatrix();
      }
    }
  });
  return null;
}

function FillFrame() {
  const setSize = useThree((s) => s.setSize);
  const gl = useThree((s) => s.gl);
  useLayoutEffect(() => {
    const el = gl.domElement.parentElement;
    if (!el) return;
    const apply = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w > 16 && h > 16) setSize(w, h);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [gl, setSize]);
  return null;
}

function GpuTune({ quality }: { quality: GraphicsQuality }) {
  const { gl } = useThree();
  useEffect(() => {
    applyRendererGpuTune(gl, quality, { exposure: quality === "high" ? 1.1 : 1.0 });
  }, [gl, quality]);
  return null;
}

function groundDetailTexture() {
  const c = document.createElement("canvas");
  c.width = 256;
  c.height = 256;
  const ctx = c.getContext("2d");
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  if (!ctx) return tex;
  c.width = 512;
  c.height = 512;
  ctx.fillStyle = "#3f7a32";
  ctx.fillRect(0, 0, 512, 512);
  for (let i = 0; i < 1800; i++) {
    const x = (i * 97) % 512;
    const y = (i * 53) % 512;
    const roll = i % 5;
    ctx.fillStyle =
      roll === 0 ? "#2d5c24" : roll === 1 ? "#4e8c3c" : roll === 2 ? "#6aaa48" : roll === 3 ? "#356828" : "#87b85a";
    ctx.fillRect(x, y, 3 + (i % 4), 2);
  }
  for (let i = 0; i < 2200; i++) {
    ctx.strokeStyle = i % 3 === 0 ? "#1e4a1c" : i % 3 === 1 ? "#8fbf62" : "#2f6a28";
    ctx.lineWidth = 1;
    ctx.beginPath();
    const x = (i * 41) % 512;
    const y = (i * 73) % 512;
    ctx.moveTo(x, y);
    ctx.lineTo(x + (i % 5) - 2, y - 6 - (i % 4));
    ctx.stroke();
  }
  for (let i = 0; i < 180; i++) {
    ctx.fillStyle = i % 2 ? "#6a4a28" : "#c4a060";
    ctx.fillRect((i * 89) % 512, (i * 37) % 512, 2, 2);
  }
  tex.needsUpdate = true;
  tex.repeat.set(14, 14);
  return tex;
}

function treeSpriteTexture() {
  const w = 256;
  const h = 384;
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = c.getContext("2d");
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  if (!ctx) return tex;
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = "#4a3018";
  ctx.fillRect(116, 292, 24, 86);
  ctx.fillStyle = "#6d4a2a";
  ctx.fillRect(122, 292, 8, 86);
  const tiers: Array<[number, number, number, string]> = [
    [318, 100, 86, "#14381f"],
    [268, 84, 76, "#1b4a2a"],
    [220, 68, 68, "#163f24"],
    [174, 52, 58, "#245636"],
    [128, 36, 50, "#1a4a2c"],
    [86, 22, 42, "#2f6a40"],
  ];
  for (const [base, half, tall, col] of tiers) {
    ctx.beginPath();
    ctx.moveTo(128, base - tall);
    ctx.lineTo(128 - half, base);
    ctx.lineTo(128 + half, base);
    ctx.closePath();
    const g = ctx.createLinearGradient(128, base - tall, 128, base);
    g.addColorStop(0, "#d5e6d4");
    g.addColorStop(0.16, col);
    g.addColorStop(1, "#0c2414");
    ctx.fillStyle = g;
    ctx.fill();
    ctx.strokeStyle = "rgba(186, 214, 170, 0.45)";
    ctx.lineWidth = 1;
    for (let i = 0; i < 16; i++) {
      const u = i / 16;
      const yy = base - 4 - u * (tall - 8);
      const reach = half * (1 - u) * 0.92;
      ctx.beginPath();
      ctx.moveTo(128 - reach, yy);
      ctx.lineTo(128 - reach - 7, yy + 5);
      ctx.moveTo(128 + reach, yy);
      ctx.lineTo(128 + reach + 7, yy + 5);
      ctx.stroke();
    }
  }
  ctx.fillStyle = "rgba(236, 244, 238, 0.9)";
  for (let i = 0; i < 26; i++) {
    ctx.fillRect(104 + ((i * 29) % 48), 36 + ((i * 17) % 78), 3, 2);
  }
  tex.needsUpdate = true;
  return tex;
}

function distToRoutes(x: number, z: number, lines: Array<Array<[number, number]>>) {
  let best = 99;
  for (const line of lines) {
    for (let i = 1; i < line.length; i++) {
      const ax = line[i - 1][0];
      const az = line[i - 1][1];
      const bx = line[i][0];
      const bz = line[i][1];
      const dx = bx - ax;
      const dz = bz - az;
      const len2 = dx * dx + dz * dz || 1;
      let u = ((x - ax) * dx + (z - az) * dz) / len2;
      if (u < 0) u = 0;
      else if (u > 1) u = 1;
      const px = ax + dx * u;
      const pz = az + dz * u;
      const d = Math.hypot(x - px, z - pz);
      if (d < best) best = d;
    }
  }
  return best;
}

function wetCell(water: WaterPayload, x: number, z: number, wet: Set<string>): boolean {
  if (!water || !wet.size) return false;
  const cell = water.cell || 2;
  const half = water.mapHalf || MAP_HALF;
  const ix = Math.floor((x + half) / cell);
  const iz = Math.floor((z + half) / cell);
  return wet.has(`${ix},${iz}`);
}

function HillTerrain({
  onGround,
  onGroundContext,
  seed,
  biome,
  water,
}: {
  onGround: (x: number, z: number) => void;
  onGroundContext: (x: number, z: number) => void;
  seed: number;
  biome?: string | null;
  water?: WaterPayload;
}) {
  const geom = useMemo(() => {
    const seg = 160;
    const span = MAP_HALF * 2 + 8;
    const g = new THREE.PlaneGeometry(span, span, seg, seg);
    g.rotateX(-Math.PI / 2);
    const pos = g.attributes.position;
    const colors = new Float32Array(pos.count * 3);
    const style = terrainStyle(biome);
    const wet = new Set(water?.cells || []);
    const routes = landRoutes(seed, biome).lines;
    const speckle = (x: number, z: number) => {
      const s = Math.sin(x * 12.9898 + z * 78.233) * 43758.5453;
      return s - Math.floor(s);
    };
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const z = pos.getZ(i);
      const soaked = wetCell(water || null, x, z, wet);
      let h = terrainHeight(x, z, seed, biome);
      if (soaked) h = 0.03;
      pos.setY(i, h);
      const slope = Math.max(
        Math.abs(terrainHeight(x + 0.8, z, seed, biome) - h),
        Math.abs(terrainHeight(x, z + 0.8, seed, biome) - h)
      );
      const cliff = slope > 0.38 || (h < WATERLINE + 0.55 && h > 0.2 && slope > 0.22);
      const n = speckle(x * 0.37, z * 0.37);
      const n2 = speckle(x * 1.4, z * 0.9);
      let r = style === "dry_arabia" ? 0.72 + n * 0.08 : 0.42 + n * 0.1;
      let gv = style === "dry_arabia" ? 0.62 + n * 0.06 : 0.58 + n2 * 0.1;
      let b = style === "dry_arabia" ? 0.38 : 0.28 + n * 0.06;
      if (style === "woodlands" && !cliff) {
        r = 0.36 + n * 0.08;
        gv = 0.52 + n * 0.08;
        b = 0.26;
      }
      if (cliff) {
        r = 0.42 + n * 0.08;
        gv = 0.4 + n * 0.05;
        b = 0.36;
      } else if (h > 1.7) {
        const t = Math.min(1, (h - 1.7) / 0.7);
        r = r * (1 - t) + 0.9 * t;
        gv = gv * (1 - t) + 0.92 * t;
        b = b * (1 - t) + 0.94 * t;
      } else if (h > 0.22 && h < WATERLINE + 1.05) {
        const t = Math.max(0, Math.min(1, (h - 0.28) / 0.85));
        r = 0.74 * (1 - t) + r * t;
        gv = 0.62 * (1 - t) + gv * t;
        b = 0.38 * (1 - t) + b * t;
      }
      if (soaked || (h < 0.22 && !cliff)) {
        r = soaked ? 0.08 : 0.1;
        gv = soaked ? 0.24 : 0.32;
        b = soaked ? 0.48 : 0.48;
      }
      const path = distToRoutes(x, z, routes);
      if (path < 0.42 && h > WATERLINE + 0.15) {
        const edge = 1 - path / 0.42;
        const grit = speckle(x * 2.8, z * 3.1);
        const worn = path < 0.16;
        const dr = (worn ? 0.55 : 0.42) + grit * 0.08;
        const dg = (worn ? 0.4 : 0.32) + grit * 0.04;
        const db = worn ? 0.24 : 0.18;
        const k = Math.min(1, edge * edge * 1.4);
        r = r * (1 - k) + dr * k;
        gv = gv * (1 - k) + dg * k;
        b = b * (1 - k) + db * k;
      }
      colors[i * 3] = r;
      colors[i * 3 + 1] = gv;
      colors[i * 3 + 2] = b;
    }
    g.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    g.computeVertexNormals();
    return g;
  }, [seed, biome, water?.cells?.length]);
  const detail = useMemo(() => groundDetailTexture(), []);
  return (
    <mesh
      geometry={geom}
      receiveShadow
      onClick={(ev) => {
        if (ev.delta > 48) return;
        onGround(ev.point.x, ev.point.z);
      }}
      onContextMenu={(ev) => {
        ev.nativeEvent.preventDefault();
        onGroundContext(ev.point.x, ev.point.z);
      }}
    >
      <meshStandardMaterial
        map={detail}
        vertexColors
        roughness={0.94}
        metalness={0}
        polygonOffset
        polygonOffsetFactor={2}
        polygonOffsetUnits={2}
      />
    </mesh>
  );
}

function FortGhost({
  cells,
  gate,
  gateMarks,
}: {
  cells: FortPoint[];
  gate: boolean;
  gateMarks: FortPoint[];
}) {
  const line = useMemo(() => {
    const g = new THREE.BufferGeometry();
    const pos = new Float32Array(Math.max(2, cells.length) * 3);
    const src = cells.length ? cells : [{ x: 0, z: 0 }];
    src.forEach((c, i) => {
      pos[i * 3] = c.x;
      pos[i * 3 + 1] = terrainHeight(c.x, c.z) + 0.38;
      pos[i * 3 + 2] = c.z;
    });
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    return g;
  }, [cells]);
  if (cells.length < 1) return null;
  const towers: FortPoint[] = [];
  const marks: FortPoint[] = [];
  for (let i = 0; i < cells.length; i++) {
    if (i > 0 && i < cells.length - 1) {
      const a = cells[i - 1];
      const b = cells[i];
      const c = cells[i + 1];
      const d1x = b.x - a.x;
      const d1z = b.z - a.z;
      const d2x = c.x - b.x;
      const d2z = c.z - b.z;
      if (d1x !== d2x || d1z !== d2z) towers.push(b);
    }
    if (i % 8 === 4) marks.push(cells[i]);
  }
  if (cells.length > 2) towers.push(cells[cells.length - 1]);
  const gateAt = (p: FortPoint) =>
    gate || gateMarks.some((g) => g.x === p.x && g.z === p.z);
  return (
    <group raycast={() => null}>
      {cells.length > 1 ? (
        <line geometry={line}>
          <lineBasicMaterial color="#f0d060" linewidth={2} />
        </line>
      ) : null}
      <mesh position={[cells[0].x, terrainHeight(cells[0].x, cells[0].z) + 0.55, cells[0].z]}>
        <cylinderGeometry args={[0.28, 0.34, 0.7, 10]} />
        <meshBasicMaterial color="#f4e2a0" transparent opacity={0.6} depthWrite={false} />
      </mesh>
      {towers.map((c, i) => (
        <mesh
          key={`tw-${c.x}-${c.z}-${i}`}
          position={[c.x, terrainHeight(c.x, c.z) + 0.7, c.z]}
        >
          <cylinderGeometry args={[0.42, 0.5, 1.15, 12]} />
          <meshBasicMaterial color="#f4e2a0" transparent opacity={0.55} depthWrite={false} />
        </mesh>
      ))}
      {(gate ? [cells[Math.floor(cells.length / 2)] || cells[0]] : marks).map((c, i) => (
        <group
          key={`inf-${c.x}-${c.z}-${i}`}
          position={[c.x, terrainHeight(c.x, c.z) + 0.85, c.z]}
        >
          <mesh position={[-0.16, 0, 0]} rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[0.16, 0.035, 6, 12]} />
            <meshBasicMaterial color={gateAt(c) ? "#fff4c8" : "#f0d060"} />
          </mesh>
          <mesh position={[0.16, 0, 0]} rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[0.16, 0.035, 6, 12]} />
            <meshBasicMaterial color={gateAt(c) ? "#fff4c8" : "#f0d060"} />
          </mesh>
        </group>
      ))}
    </group>
  );
}

function Scene({
  entities,
  selectedIds,
  onSelect,
  onGround,
  onGroundContext,
  onEntityContext,
  panSpeed,
  fog,
  water,
  beaches,
  panApiRef,
  dragPan,
  orbitNudge,
  edgePan,
  quality,
  myTeam,
  fortPreview,
  fortPreviewGate,
  fortGateMarks,
  mapSeed,
  mapBiome,
  lookAt = null,
}: {
  entities: PublicEntity[];
  selectedIds: string[];
  onSelect: (id: string, shift: boolean, alt?: boolean) => void;
  onGround: (x: number, z: number) => void;
  onGroundContext: (x: number, z: number) => void;
  onEntityContext: (id: string) => void;
  panSpeed: number;
  fog: FogPayload;
  water: WaterPayload;
  beaches?: string[];
  panApiRef: React.MutableRefObject<PanApi | null>;
  dragPan: React.MutableRefObject<{ dx: number; dz: number }>;
  orbitNudge: React.MutableRefObject<{ yaw: number; pitch: number }>;
  edgePan: React.MutableRefObject<{ x: number; z: number }>;
  quality: GraphicsQuality;
  myTeam: string | null;
  fortPreview: FortPoint[];
  fortPreviewGate: boolean;
  fortGateMarks: FortPoint[];
  mapSeed: number;
  mapBiome?: string | null;
  lookAt?: { x: number; z: number; n: number } | null;
}) {
  const sel = useMemo(() => new Set(selectedIds), [selectedIds]);
  const textures = useEmpireTextures(quality);
  const shadow = shadowMapSize(quality);
  const fortMasks = useMemo(() => {
    const FORT = new Set(["palisade", "wall", "fortified_wall", "gate"]);
    const map = new Map<string, number>();
    const forts = entities.filter((e) => e.hp > 0 && FORT.has(e.kind));
    for (const e of forts) {
      const gx = Math.round(e.x);
      const gz = Math.round(e.z);
      let m = 0;
      const has = (dx: number, dz: number) =>
        forts.some(
          (o) => Math.round(o.x) === gx + dx && Math.round(o.z) === gz + dz
        );
      if (has(0, 1)) m |= 1;
      if (has(1, 0)) m |= 2;
      if (has(0, -1)) m |= 4;
      if (has(-1, 0)) m |= 8;
      map.set(e.id, m);
    }
    return map;
  }, [entities]);
  const focus = useMemo((): [number, number, number] => {
    if (lookAt) return [lookAt.x, 0, lookAt.z];
    const hall =
      (myTeam
        ? entities.find(
            (e) => e.kind === "town_hall" && e.team === myTeam && e.hp > 0
          )
        : null) ||
      entities.find((e) => e.kind === "town_hall" && e.hp > 0);
    if (hall) return [hall.x, 0, hall.z];
    const workers = entities.filter(
      (e) => e.kind === "worker" && (!myTeam || e.team === myTeam) && e.hp > 0
    );
    if (workers.length) {
      const x = workers.reduce((s, w) => s + w.x, 0) / workers.length;
      const z = workers.reduce((s, w) => s + w.z, 0) / workers.length;
      return [x, 0, z];
    }
    // Default amber base so solo practice isn't aimed at map center void
    return myTeam === "teal" ? [20, 0, 20] : [-20, 0, -20];
  }, [entities, myTeam, lookAt]);
  const chopTrees = useMemo(() => {
    const painted = [...terrainTrees(mapSeed, mapBiome), ...avenueTrees(mapSeed, mapBiome)];
    const wood = entities.filter((e) => e.kind === "wood");
    const stumps = wood.filter((e) => e.hp <= 0 || (e.stock ?? 1) <= 0);
    const wet = new Set(water?.cells || []);
    const standing = painted.filter(
      (t) =>
        !wetCell(water, t.x, t.z, wet) &&
        !stumps.some((s) => Math.hypot(s.x - t.x, s.z - t.z) < 1.8)
    );
    const extra = wood
      .filter(
        (e) =>
          e.hp > 0 &&
          (e.stock ?? 1) > 0 &&
          !painted.some((t) => Math.hypot(t.x - e.x, t.z - e.z) < 1.8)
      )
      .map((e, i) => ({
        x: e.x,
        z: e.z,
        s: 0.9 + ((i * 13) % 8) / 20,
        hue: (i * 0.17) % 1,
      }));
    return [...standing, ...extra];
  }, [entities, mapSeed, mapBiome, water?.cells?.length]);

  return (
    <>
      <FillFrame />
      <GpuTune quality={quality} />
      <color
        attach="background"
        args={[terrainStyle(mapBiome) === "dry_arabia" ? "#b08958" : "#1c3326"]}
      />
      <fog attach="fog" args={["#3a5040", 70, quality === "low" ? 140 : 180]} />
      <ambientLight intensity={quality === "low" ? 0.7 : 0.52} color="#fff4e0" />
      <hemisphereLight args={["#d8ecff", "#3a5a28", 0.58]} />
      <directionalLight
        castShadow={quality !== "low"}
        position={[14, 22, 8]}
        intensity={quality === "high" ? 1.15 : 0.92}
        color="#fff2d6"
        shadow-mapSize-width={shadow}
        shadow-mapSize-height={shadow}
        shadow-camera-near={1}
        shadow-camera-far={90}
        shadow-camera-left={-36}
        shadow-camera-right={36}
        shadow-camera-top={36}
        shadow-camera-bottom={-36}
        shadow-bias={-0.00025}
      />
      <directionalLight intensity={0.8} position={[-12, 16, -10]} color="#ffe0c0" />
      {quality !== "low" ? (
        <Suspense fallback={null}>
          <Environment preset="park" environmentIntensity={0.32} />
        </Suspense>
      ) : null}
      <HillTerrain
        seed={mapSeed}
        biome={mapBiome}
        water={water}
        onGround={onGround}
        onGroundContext={onGroundContext}
      />
      <RoadNet seed={mapSeed} biome={mapBiome} />
      <InstancedForest seed={mapSeed} biome={mapBiome} trees={chopTrees} />
      <LandmarkSites seed={mapSeed} biome={mapBiome} />
      <WaterLayer water={water} seed={mapSeed} />
      <BeachSandLayer beaches={beaches} water={water} />
      {entities.filter((e) => e.hp > 0 && !e.insideId).map((e) => (
        <EntityView
          key={e.id}
          e={e}
          selected={sel.has(e.id)}
          onSelect={onSelect}
          onContext={onEntityContext}
          farmTex={textures.farm}
          fortMask={fortMasks.get(e.id) || 0}
        />
      ))}
      {fortPreview.length > 0 ? (
        <FortGhost
          cells={fortPreview}
          gate={fortPreviewGate}
          gateMarks={fortGateMarks}
        />
      ) : null}
      <SoftFogLayer fog={fog} quality={quality} />
      {quality !== "low" ? (
        <ContactShadows
          opacity={0.38}
          scale={68}
          blur={2.6}
          far={14}
          color="#2a2010"
        />
      ) : null}
      <IsoCamera
        focus={focus}
        panSpeed={panSpeed}
        panApiRef={panApiRef}
        dragPan={dragPan}
        orbitNudge={orbitNudge}
        edgePan={edgePan}
      />
    </>
  );
}

function Minimap({
  entities,
  fog,
  myTeam,
  onPan,
}: {
  entities: PublicEntity[];
  fog: FogPayload;
  myTeam: string | null;
  onPan: (x: number, z: number) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const half = fog?.mapHalf || 32;

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    if (!ctx) return;
    const W = c.width;
    const H = c.height;
    ctx.fillStyle = "#0a0a0e";
    ctx.fillRect(0, 0, W, H);

    const toPx = (x: number, z: number) => ({
      px: ((x + half) / (half * 2)) * W,
      py: ((z + half) / (half * 2)) * H,
    });

    // Always paint grass base — fog darkens, never pure black void
    ctx.fillStyle = "#1a3020";
    ctx.fillRect(0, 0, W, H);
    if (fog) {
      const cell = fog.cell || 2;
      const explored = new Set(fog.explored);
      const visible = new Set(fog.visible);
      const n = Math.ceil((half * 2) / cell);
      for (let ix = 0; ix < n; ix++) {
        for (let iz = 0; iz < n; iz++) {
          const key = `${ix},${iz}`;
          const x = -half + ix * cell;
          const z = -half + iz * cell;
          const { px, py } = toPx(x, z);
          const s = (cell / (half * 2)) * W + 1;
          if (visible.has(key)) {
            ctx.fillStyle = "#4a8a38";
            ctx.fillRect(px, py, s, s);
          } else if (explored.has(key)) {
            ctx.fillStyle = "#2a4a28";
            ctx.fillRect(px, py, s, s);
          } else {
            ctx.fillStyle = "#121c14";
            ctx.fillRect(px, py, s, s);
          }
        }
      }
    } else {
      ctx.fillStyle = "#2a4a28";
      ctx.fillRect(0, 0, W, H);
    }

    for (const e of entities) {
      const { px, py } = toPx(e.x, e.z);
      if (e.kind === "chrono") {
        ctx.fillStyle = "#ffd700";
        ctx.beginPath();
        ctx.arc(px, py, 3, 0, Math.PI * 2);
        ctx.fill();
        continue;
      }
      if (e.kind === "wood") ctx.fillStyle = "#2f6b3a";
      else if (e.kind === "food" || e.kind === "fish") ctx.fillStyle = "#c45c2a";
      else if (e.kind === "gold") ctx.fillStyle = "#e8c040";
      else if (e.kind === "stone") ctx.fillStyle = "#a0a098";
      else if (e.kind === "wall" || e.kind === "gate") ctx.fillStyle = "#888070";
      else if (e.team && TEAM_BANNER[e.team]) ctx.fillStyle = TEAM_BANNER[e.team];
      else ctx.fillStyle = "#888";
      const r =
        e.kind === "town_hall" || e.kind === "keep"
          ? 4
          : e.kind === "wall"
            ? 2
            : e.team
              ? 3
              : 2;
      ctx.fillRect(px - r / 2, py - r / 2, r, r);
    }
    if (myTeam) {
      ctx.strokeStyle = "#f4e8c0";
      ctx.lineWidth = 2;
      ctx.strokeRect(1, 1, W - 2, H - 2);
    }
  }, [entities, fog, half, myTeam]);

  return (
    <canvas
      ref={canvasRef}
      width={140}
      height={140}
      className="empire-minimap"
      title="Minimap — click to pan"
      onClick={(ev) => {
        const rect = ev.currentTarget.getBoundingClientRect();
        const u = (ev.clientX - rect.left) / rect.width;
        const v = (ev.clientY - rect.top) / rect.height;
        const x = -half + u * half * 2;
        const z = -half + v * half * 2;
        onPan(x, z);
      }}
    />
  );
}

const UNIT_KINDS = new Set([
  "worker", "militia", "spearman", "man_at_arms",
  "archer", "skirmisher", "crossbowman", "hand_cannon",
  "scout_cav", "knight", "camel_rider", "war_elephant",
  "ram", "mangonel", "scorpion", "trebuchet",
  "fishing_boat", "transport", "war_galley",
  "geologist", "healer", "explorer", "trade_cart",
]);

const BUILDING_KINDS = new Set([
  "town_hall", "house", "farm", "mill", "lumber_camp", "mining_camp",
  "barracks", "archery_range", "stable", "blacksmith", "university",
  "market", "dock", "watch_tower", "palisade", "wall", "fortified_wall",
  "gate", "siege_workshop", "keep", "church",
]);

/** How far a click can land from the entity and still count. Buildings are tall, so the ground point under the roof sits well away from the center. */
function pickReach(kind: string): number {
  if (kind === "town_hall" || kind === "keep") return 5.4;
  if (kind === "farm") return 3.4;
  if (kind === "wall" || kind === "fortified_wall" || kind === "palisade" || kind === "gate") return 1.7;
  if (BUILDING_KINDS.has(kind)) return 4.2;
  if (kind === "wood" || kind === "food" || kind === "gold" || kind === "stone" || kind === "fish") return 2.2;
  return 1.15;
}

/** Closest thing under the cursor. A click on a person selects that person, even beside the town hall. */
function pickEntity(
  entities: PublicEntity[],
  x: number,
  z: number,
  preferUnits = false
): PublicEntity | null {
  let bestUnit: PublicEntity | null = null;
  let bestUnitD = preferUnits ? 1.05 : 0;
  let best: PublicEntity | null = null;
  let bestScore = Infinity;
  for (const en of entities) {
    if (en.hp <= 0) continue;
    const unit = UNIT_KINDS.has(en.kind);
    const d = Math.hypot(en.x - x, en.z - z);
    if (preferUnits && unit && d < bestUnitD) {
      bestUnit = en;
      bestUnitD = d;
    }
    const reach = pickReach(en.kind);
    if (d > reach) continue;
    const score = unit ? d / 0.85 : d / reach;
    if (score < bestScore) {
      best = en;
      bestScore = score;
    }
  }
  if (bestUnit) return bestUnit;
  return best;
}

/** Right-click only counts as a tree, building, or person when the pointer is on them. Nearby grass is a move. */
function orderReach(kind: string): number {
  if (kind === "wood" || kind === "food" || kind === "gold" || kind === "stone" || kind === "fish") return 1.2;
  if (kind === "farm") return 1.8;
  if (kind === "town_hall" || kind === "keep") return 2.2;
  if (BUILDING_KINDS.has(kind)) return 1.8;
  if (
    kind === "sheep" ||
    kind === "chicken" ||
    kind === "cow" ||
    kind === "goat" ||
    kind === "duck" ||
    kind === "deer" ||
    kind === "lion" ||
    kind === "coyote" ||
    kind === "gorilla"
  ) {
    return 1.3;
  }
  return 0.7;
}

function pickRightClickTarget(
  entities: PublicEntity[],
  x: number,
  z: number
): PublicEntity | null {
  let best: PublicEntity | null = null;
  let bestD = Infinity;
  for (const en of entities) {
    if (en.hp <= 0) continue;
    const d = Math.hypot(en.x - x, en.z - z);
    if (d > orderReach(en.kind) || d >= bestD) continue;
    best = en;
    bestD = d;
  }
  return best;
}

type JobCursorName = "axe" | "shovel" | "meat" | "peanuts";

const FOOD_CURSOR_KINDS = new Set([
  "deer",
  "chicken",
  "sheep",
  "cow",
  "goat",
  "duck",
]);

/** Gather-job icon for the entity a right-click would order. */
function jobCursorForKind(kind: string): JobCursorName | null {
  if (kind === "wood") return "axe";
  if (kind === "gold" || kind === "stone" || kind === "silver") return "shovel";
  if (kind === "farm" || kind === "food") return "peanuts";
  if (kind === "fish") return "meat";
  if (FOOD_CURSOR_KINDS.has(kind)) return "meat";
  return null;
}

/** Bitmap size of the gather cursor. Drawings are laid out in a 48px box and scaled down to a normal pointer. */
const CURSOR_DRAW = 48;
const CURSOR_PX = 20;

function traceStroke(
  ctx: CanvasRenderingContext2D,
  trace: () => void,
  width: number,
  color: string
) {
  ctx.beginPath();
  trace();
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.stroke();
}

function traceFill(
  ctx: CanvasRenderingContext2D,
  trace: () => void,
  fill: string,
  stroke: string,
  strokeWidth: number
) {
  ctx.beginPath();
  trace();
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.lineWidth = strokeWidth + 5;
  ctx.strokeStyle = "#f6f1e2";
  ctx.stroke();
  ctx.lineWidth = strokeWidth + 1.5;
  ctx.strokeStyle = "#140e0a";
  ctx.stroke();
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.lineWidth = strokeWidth;
  ctx.strokeStyle = stroke;
  ctx.stroke();
}

function drawAxe(ctx: CanvasRenderingContext2D): { x: number; y: number } {
  const handle = () => {
    ctx.moveTo(20, 18);
    ctx.lineTo(38, 42);
  };
  ctx.lineCap = "round";
  ctx.beginPath();
  handle();
  ctx.lineWidth = 9;
  ctx.strokeStyle = "#f6f1e2";
  ctx.stroke();
  ctx.beginPath();
  handle();
  ctx.lineWidth = 5.5;
  ctx.strokeStyle = "#140e0a";
  ctx.stroke();
  ctx.beginPath();
  handle();
  ctx.lineWidth = 3.2;
  ctx.strokeStyle = "#a56b32";
  ctx.stroke();
  const blade = () => {
    ctx.moveTo(6, 7);
    ctx.lineTo(22, 13);
    ctx.quadraticCurveTo(26, 20, 20, 27);
    ctx.lineTo(6, 31);
    ctx.quadraticCurveTo(3, 19, 6, 7);
  };
  traceFill(ctx, blade, "#e4eaf1", "#2a3340", 1.4);
  ctx.beginPath();
  ctx.moveTo(7, 11);
  ctx.lineTo(7, 27);
  ctx.strokeStyle = "#ffffff";
  ctx.lineWidth = 1.6;
  ctx.stroke();
  return { x: 6, y: 18 };
}

function drawShovel(ctx: CanvasRenderingContext2D): { x: number; y: number } {
  const handle = () => {
    ctx.moveTo(24, 5);
    ctx.lineTo(24, 26);
  };
  ctx.lineCap = "round";
  ctx.beginPath();
  handle();
  ctx.lineWidth = 8;
  ctx.strokeStyle = "#f6f1e2";
  ctx.stroke();
  ctx.beginPath();
  handle();
  ctx.lineWidth = 4.5;
  ctx.strokeStyle = "#140e0a";
  ctx.stroke();
  ctx.beginPath();
  handle();
  ctx.lineWidth = 2.4;
  ctx.strokeStyle = "#c4924a";
  ctx.stroke();
  const grip = () => {
    ctx.arc(24, 6, 4.2, Math.PI * 0.15, Math.PI * 0.85, true);
  };
  traceStroke(ctx, grip, 7, "#f6f1e2");
  traceStroke(ctx, grip, 3.2, "#5c3a1e");
  const blade = () => {
    ctx.moveTo(24, 22);
    ctx.quadraticCurveTo(42, 30, 24, 45);
    ctx.quadraticCurveTo(6, 30, 24, 22);
  };
  traceFill(ctx, blade, "#c5ccd4", "#2c333c", 1.3);
  ctx.beginPath();
  ctx.moveTo(24, 26);
  ctx.lineTo(24, 42);
  ctx.strokeStyle = "rgba(255,255,255,0.85)";
  ctx.lineWidth = 1.3;
  ctx.stroke();
  return { x: 24, y: 44 };
}

function drawMeat(ctx: CanvasRenderingContext2D): { x: number; y: number } {
  const bone = () => {
    ctx.moveTo(26, 24);
    ctx.lineTo(38, 38);
  };
  ctx.lineCap = "round";
  ctx.beginPath();
  bone();
  ctx.lineWidth = 8;
  ctx.strokeStyle = "#f6f1e2";
  ctx.stroke();
  ctx.beginPath();
  bone();
  ctx.lineWidth = 4;
  ctx.strokeStyle = "#f4efe4";
  ctx.stroke();
  ctx.beginPath();
  bone();
  ctx.lineWidth = 1.2;
  ctx.strokeStyle = "#8a8174";
  ctx.stroke();
  const knob = () => {
    ctx.arc(40, 40, 4.2, 0, Math.PI * 2);
  };
  traceFill(ctx, knob, "#f7f3ea", "#6e655c", 1.2);
  const chop = () => {
    ctx.ellipse(16, 16, 13, 10, -0.6, 0, Math.PI * 2);
  };
  traceFill(ctx, chop, "#d24a3c", "#6a1c16", 1.3);
  ctx.beginPath();
  ctx.ellipse(14, 14, 6, 3.2, -0.7, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(255, 220, 200, 0.85)";
  ctx.fill();
  return { x: 6, y: 12 };
}

function drawPeanuts(ctx: CanvasRenderingContext2D): { x: number; y: number } {
  const shell = (ox: number, oy: number, rot: number) => {
    ctx.save();
    ctx.translate(ox, oy);
    ctx.rotate(rot);
    ctx.beginPath();
    ctx.moveTo(0, -15);
    ctx.bezierCurveTo(9, -15, 8, -2, 0, 0);
    ctx.bezierCurveTo(-8, -2, -9, -15, 0, -15);
    ctx.moveTo(0, 0);
    ctx.bezierCurveTo(8, 2, 8, 15, 0, 15);
    ctx.bezierCurveTo(-8, 15, -8, 2, 0, 0);
    ctx.restore();
  };
  const paint = (ox: number, oy: number, rot: number) => {
    const trace = () => shell(ox, oy, rot);
    traceFill(ctx, trace, "#e7c56a", "#6a4318", 1.2);
    ctx.save();
    ctx.translate(ox, oy);
    ctx.rotate(rot);
    ctx.beginPath();
    ctx.moveTo(0, -11);
    ctx.lineTo(0, 11);
    ctx.strokeStyle = "rgba(90, 52, 16, 0.55)";
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();
  };
  paint(30, 28, 0.5);
  paint(18, 22, -0.35);
  return { x: 13, y: 8 };
}

let jobCursorCssCache: Record<JobCursorName, string> | null = null;

function jobCursorCss(): Record<JobCursorName, string> {
  if (jobCursorCssCache) return jobCursorCssCache;
  const make = (draw: (ctx: CanvasRenderingContext2D) => { x: number; y: number }) => {
    const canvas = document.createElement("canvas");
    canvas.width = CURSOR_PX;
    canvas.height = CURSOR_PX;
    const ctx = canvas.getContext("2d");
    if (!ctx) return "auto";
    const fit = CURSOR_PX / CURSOR_DRAW;
    ctx.scale(fit, fit);
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    const hot = draw(ctx);
    const hx = Math.max(0, Math.min(CURSOR_PX - 1, Math.round(hot.x * fit)));
    const hy = Math.max(0, Math.min(CURSOR_PX - 1, Math.round(hot.y * fit)));
    return `url("${canvas.toDataURL("image/png")}") ${hx} ${hy}, auto`;
  };
  jobCursorCssCache = {
    axe: make(drawAxe),
    shovel: make(drawShovel),
    meat: make(drawMeat),
    peanuts: make(drawPeanuts),
  };
  return jobCursorCssCache;
}

export default function EmpireWorld3D({
  entities,
  selectedIds,
  onSelect,
  onBoxSelect,
  onGroundClick,
  onGroundRightClick,
  onEntityRightClick,
  onFortBuildLine,
  onFortCancel,
  attackMoveArmed = false,
  onAttackMove,
  fortBuildMode = false,
  placeBuildMode = false,
  fortBuildGate = false,
  panSpeed = 1,
  fog = null,
  water = null,
  beaches,
  myTeam = null,
  interactive = true,
  quality = "high",
  preferHighPerfGpu = true,
  mapSeed = 11,
  mapBiome = null,
  lookAt = null,
  children,
}: {
  entities: PublicEntity[];
  selectedIds: string[];
  onSelect: (id: string, shift: boolean, alt?: boolean) => void;
  onBoxSelect?: (ids: string[], additive?: boolean) => void;
  onGroundClick: (x: number, z: number) => void;
  onGroundRightClick: (x: number, z: number, shift?: boolean) => void;
  onEntityRightClick: (id: string, shift?: boolean, alt?: boolean) => void;
  /** Commit one wall segment (any angle). Gates are cells that become gatehouses. */
  onFortBuildLine?: (
    x0: number,
    z0: number,
    x1: number,
    z1: number,
    path?: FortPoint[],
    gates?: FortPoint[]
  ) => void;
  /** Right-click or Escape leaves the wall chain without placing the live tail. */
  onFortCancel?: () => void;
  attackMoveArmed?: boolean;
  onAttackMove?: (x: number, z: number) => void;
  /** When true, LMB click-chains a fort line instead of box-select. */
  fortBuildMode?: boolean;
  /** House / farm / dock / etc — click places, box-select stays off. */
  placeBuildMode?: boolean;
  /** Gate preview places one gatehouse (midpoint). */
  fortBuildGate?: boolean;
  panSpeed?: number;
  fog?: FogPayload;
  water?: WaterPayload;
  beaches?: string[];
  myTeam?: string | null;
  interactive?: boolean;
  quality?: GraphicsQuality;
  preferHighPerfGpu?: boolean;
  mapSeed?: number | null;
  mapBiome?: string | null;
  lookAt?: { x: number; z: number; n: number } | null;
  children?: React.ReactNode;
}) {
  const seed = mapSeed ?? 11;
  bindTerrainView(seed, mapBiome);
  useEffect(() => {
    const kick = () => window.dispatchEvent(new Event("resize"));
    kick();
    const a = window.setTimeout(kick, 60);
    const b = window.setTimeout(kick, 420);
    return () => {
      window.clearTimeout(a);
      window.clearTimeout(b);
    };
  }, []);
  const wrapRef = useRef<HTMLDivElement>(null);
  const panApiRef = useRef<PanApi | null>(null);
  /** After a real box-drag, ignore the trailing mesh click that would wipe multi-select. */
  const skipEntityClickRef = useRef(false);
  /** After fort line commit, ignore the trailing mesh click. */
  const skipFortClickRef = useRef(false);
  const dragPan = useRef({ dx: 0, dz: 0 });
  const orbitNudge = useRef({ yaw: 0, pitch: 0 });
  const edgePan = useRef({ x: 0, z: 0 });
  const boxRef = useRef<HTMLDivElement>(null);
  const fortBuildModeRef = useRef(fortBuildMode);
  fortBuildModeRef.current = fortBuildMode;
  const placeBuildModeRef = useRef(placeBuildMode);
  placeBuildModeRef.current = placeBuildMode;
  const lastPlaceAt = useRef(0);
  const fortChain = useRef<{ points: FortPoint[]; gates: FortPoint[] } | null>(null);
  const attackMoveRef = useRef(attackMoveArmed);
  attackMoveRef.current = attackMoveArmed;
  const onAttackMoveRef = useRef(onAttackMove);
  onAttackMoveRef.current = onAttackMove;
  const onFortCancelRef = useRef(onFortCancel);
  onFortCancelRef.current = onFortCancel;
  const fortBuildGateRef = useRef(fortBuildGate);
  fortBuildGateRef.current = fortBuildGate;
  const lastPick = useRef({ id: "", t: 0 });
  const drag = useRef<{
    mode: "none" | "box" | "pan" | "fort" | "order" | "place";
    x0: number;
    y0: number;
    lx: number;
    ly: number;
  }>({ mode: "none", x0: 0, y0: 0, lx: 0, ly: 0 });
  const entitiesRef = useRef(entities);
  entitiesRef.current = entities;
  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;
  const myTeamRef = useRef(myTeam);
  myTeamRef.current = myTeam;
  const pointerRef = useRef<{ x: number; y: number } | null>(null);
  const jobCursorNameRef = useRef("");
  const [webglOk, setWebglOk] = useState(true);
  const [webglMsg, setWebglMsg] = useState("");
  const [fortPreview, setFortPreview] = useState<FortPoint[]>([]);
  const [fortGateMarks, setFortGateMarks] = useState<FortPoint[]>([]);

  useEffect(() => {
    const p = probeWebGL();
    setWebglOk(p.ok);
    setWebglMsg(p.message || "");
  }, []);

  useEffect(() => {
    if (!fortBuildMode) {
      fortChain.current = null;
      setFortPreview([]);
      setFortGateMarks([]);
      if (drag.current.mode === "fort") drag.current.mode = "none";
    }
  }, [fortBuildMode]);

  useEffect(() => {
    if (!interactive) return;
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (e.key.toLowerCase() !== "g" || !fortBuildModeRef.current) return;
      const p = pointerRef.current;
      if (!p) return;
      const w = panApiRef.current?.clientToWorld(p.x, p.y);
      if (!w) return;
      const cell = { x: Math.round(w.x), z: Math.round(w.z) };
      const chain = fortChain.current || { points: [], gates: [] };
      const idx = chain.gates.findIndex((g) => g.x === cell.x && g.z === cell.z);
      if (idx >= 0) chain.gates.splice(idx, 1);
      else chain.gates.push(cell);
      fortChain.current = chain;
      setFortGateMarks([...chain.gates]);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [interactive]);

  const syncJobCursor = useCallback((clientX: number, clientY: number) => {
    const canvas = wrapRef.current?.querySelector(
      ".empire-canvas-wrap canvas"
    ) as HTMLCanvasElement | null;
    if (!canvas) return;
    let name: JobCursorName | "" = "";
    const mode = drag.current.mode;
    const leftDrag =
      mode === "box" || mode === "pan" || mode === "fort" || mode === "place";
    const rect = canvas.getBoundingClientRect();
    const inside =
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom;
    const team = myTeamRef.current;
    if (!leftDrag && inside && team) {
      const ents = entitiesRef.current;
      const workersReady = selectedIdsRef.current.some((id) => {
        const e = ents.find((en) => en.id === id);
        return !!e && e.kind === "worker" && e.team === team && e.hp > 0;
      });
      if (workersReady) {
        const w = panApiRef.current?.clientToWorld(clientX, clientY);
        if (w) {
          const hit = pickRightClickTarget(ents, w.x, w.z);
          const job =
            hit && !(hit.constructing && hit.kind === "farm")
              ? jobCursorForKind(hit.kind)
              : null;
          if (job) name = job;
        }
      }
    }
    if (jobCursorNameRef.current === name) return;
    jobCursorNameRef.current = name;
    canvas.dataset.orderCursor = name;
    if (!name) {
      canvas.style.removeProperty("cursor");
      return;
    }
    canvas.style.setProperty("cursor", jobCursorCss()[name], "important");
  }, []);

  useEffect(() => {
    if (!interactive) return;
    const onPoint = (e: PointerEvent) => {
      pointerRef.current = { x: e.clientX, y: e.clientY };
      syncJobCursor(e.clientX, e.clientY);
    };
    window.addEventListener("pointermove", onPoint);
    window.addEventListener("pointerdown", onPoint);
    window.addEventListener("pointerup", onPoint);
    return () => {
      window.removeEventListener("pointermove", onPoint);
      window.removeEventListener("pointerdown", onPoint);
      window.removeEventListener("pointerup", onPoint);
      jobCursorNameRef.current = "";
      const canvas = wrapRef.current?.querySelector(
        ".empire-canvas-wrap canvas"
      ) as HTMLCanvasElement | null;
      if (canvas) {
        canvas.style.removeProperty("cursor");
        delete canvas.dataset.orderCursor;
      }
    };
  }, [interactive, syncJobCursor]);

  useEffect(() => {
    const p = pointerRef.current;
    if (p) syncJobCursor(p.x, p.y);
  }, [entities, selectedIds, myTeam, syncJobCursor]);

  const onPanMinimap = useCallback((x: number, z: number) => {
    panApiRef.current?.panTo(x, z);
  }, []);

  const previewFromDrag = useCallback((corners: FortPoint[], cur: FortPoint) => {
    const pts = [...corners];
    const last = pts[pts.length - 1];
    if (!last || last.x !== cur.x || last.z !== cur.z) pts.push(cur);
    return fortCellsAlongCorners(pts, FORT_LINE_MAX);
  }, []);

  /** Project cursor onto locked fort axis from segment anchor (AoE-style). */
  const projectFortAxis = useCallback(
    (anchor: FortPoint, cell: FortPoint, axis: "x" | "z" | "d"): FortPoint => {
      if (axis === "x") return { x: cell.x, z: anchor.z };
      if (axis === "z") return { x: anchor.x, z: cell.z };
      const dx = cell.x - anchor.x;
      const dz = cell.z - anchor.z;
      const steps = Math.min(Math.abs(dx), Math.abs(dz));
      const sx = dx === 0 ? 0 : dx > 0 ? 1 : -1;
      const sz = dz === 0 ? 0 : dz > 0 ? 1 : -1;
      return { x: anchor.x + sx * steps, z: anchor.z + sz * steps };
    },
    []
  );

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onCtx = (e: Event) => e.preventDefault();
    el.addEventListener("contextmenu", onCtx);
    return () => el.removeEventListener("contextmenu", onCtx);
  }, []);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || !interactive) return;

    const cellAt = (clientX: number, clientY: number): FortPoint | null => {
      const w = panApiRef.current?.clientToWorld(clientX, clientY);
      if (!w) return null;
      return { x: Math.round(w.x), z: Math.round(w.z) };
    };

    const onMap = (e: { target: EventTarget | null }) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest?.(".empire-minimap, .empire-chat-dock, button, a, input, textarea")) return false;
      return true;
    };

    const placeArmed = (clientX: number, clientY: number) => {
      if (!placeBuildModeRef.current) return false;
      const now = performance.now();
      if (now - lastPlaceAt.current < 450) return true;
      const w = panApiRef.current?.clientToWorld(clientX, clientY);
      if (!w) return false;
      lastPlaceAt.current = now;
      onGroundClick(w.x, w.z);
      return true;
    };

    const onDown = (e: PointerEvent) => {
      if (!onMap(e)) return;
      if (e.button === 1) {
        drag.current = {
          mode: "pan",
          x0: e.clientX,
          y0: e.clientY,
          lx: e.clientX,
          ly: e.clientY,
        };
        return;
      }
      if (e.button === 2 || (e.button === 0 && e.ctrlKey)) {
        if (e.button === 2 && fortBuildModeRef.current) {
          fortChain.current = null;
          setFortPreview([]);
          setFortGateMarks([]);
          drag.current.mode = "none";
          onFortCancelRef.current?.();
          return;
        }
        drag.current = {
          mode: "order",
          x0: e.clientX,
          y0: e.clientY,
          lx: e.clientX,
          ly: e.clientY,
        };
        try {
          el.setPointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
        return;
      }
      if (e.button === 0) {
        if (fortBuildModeRef.current && onFortBuildLine && !e.altKey) {
          drag.current = {
            mode: "fort",
            x0: e.clientX,
            y0: e.clientY,
            lx: e.clientX,
            ly: e.clientY,
          };
          return;
        }
        if (placeBuildModeRef.current) {
          drag.current = {
            mode: "place",
            x0: e.clientX,
            y0: e.clientY,
            lx: e.clientX,
            ly: e.clientY,
          };
          skipEntityClickRef.current = true;
          skipFortClickRef.current = true;
          try {
            el.setPointerCapture(e.pointerId);
          } catch {
            /* ignore */
          }
          return;
        }
        drag.current = {
          mode: "box",
          x0: e.clientX,
          y0: e.clientY,
          lx: e.clientX,
          ly: e.clientY,
        };
        if (boxRef.current) {
          boxRef.current.style.display = "none";
        }
      }
    };
    const paintGhost = (cell: FortPoint | null) => {
      const chain = fortChain.current;
      if (!chain?.points.length) {
        setFortPreview(cell ? [cell] : []);
        return;
      }
      const pts = [...chain.points];
      const last = pts[pts.length - 1];
      if (cell && (cell.x !== last.x || cell.z !== last.z)) pts.push(cell);
      setFortPreview(pts.length >= 2 ? fortCellsAlongCorners(pts, FORT_LINE_MAX) : pts);
    };
    const onMove = (e: PointerEvent) => {
      const stage = el.getBoundingClientRect();
      const margin = 28;
      let sx = 0;
      let sy = 0;
      if (e.clientX < stage.left + margin) sx = -1;
      else if (e.clientX > stage.right - margin) sx = 1;
      if (e.clientY < stage.top + margin) sy = 1;
      else if (e.clientY > stage.bottom - margin) sy = -1;
      edgePan.current = { x: sx, z: sy };
      if (fortBuildModeRef.current) {
        paintGhost(cellAt(e.clientX, e.clientY));
      }
      if (drag.current.mode === "pan") {
        const dx = e.clientX - drag.current.lx;
        const dy = e.clientY - drag.current.ly;
        drag.current.lx = e.clientX;
        drag.current.ly = e.clientY;
        orbitNudge.current.yaw -= dx * 0.006;
        orbitNudge.current.pitch += dy * 0.004;
        return;
      }
      if (drag.current.mode === "box" && boxRef.current) {
        const stage = el.getBoundingClientRect();
        const x1 = Math.min(drag.current.x0, e.clientX) - stage.left;
        const y1 = Math.min(drag.current.y0, e.clientY) - stage.top;
        const w = Math.abs(e.clientX - drag.current.x0);
        const h = Math.abs(e.clientY - drag.current.y0);
        if (w > 6 || h > 6) {
          const b = boxRef.current;
          b.style.display = "block";
          b.style.left = `${x1}px`;
          b.style.top = `${y1}px`;
          b.style.width = `${w}px`;
          b.style.height = `${h}px`;
        }
      }
    };
    const onUp = (e: PointerEvent) => {
      if (drag.current.mode === "order") {
        drag.current.mode = "none";
        skipEntityClickRef.current = true;
        window.setTimeout(() => {
          skipEntityClickRef.current = false;
        }, 80);
        const w = panApiRef.current?.clientToWorld(e.clientX, e.clientY);
        if (!w) return;
        const best = pickRightClickTarget(entities, w.x, w.z);
        const ownBuilding =
          !!best &&
          best.team === myTeamRef.current &&
          (best.kind === "town_hall" || BUILDING_KINDS.has(best.kind)) &&
          !e.altKey &&
          best.hp >= best.maxHp - 0.5;
        if (best && !ownBuilding) onEntityRightClick(best.id, e.shiftKey, e.altKey);
        else onGroundRightClick(w.x, w.z, e.shiftKey);
        return;
      }
      if (drag.current.mode === "place") {
        drag.current.mode = "none";
        placeArmed(e.clientX, e.clientY);
        window.setTimeout(() => {
          skipEntityClickRef.current = false;
          skipFortClickRef.current = false;
        }, 200);
        return;
      }
      if (drag.current.mode === "fort") {
        const cell = cellAt(e.clientX, e.clientY);
        drag.current.mode = "none";
        skipFortClickRef.current = true;
        window.setTimeout(() => {
          skipFortClickRef.current = false;
        }, 0);
        if (!cell || !onFortBuildLine) return;
        if (fortBuildGateRef.current) {
          onFortBuildLine(cell.x, cell.z, cell.x, cell.z);
          return;
        }
        const chain = fortChain.current;
        if (!chain || !chain.points.length) {
          fortChain.current = { points: [cell], gates: chain?.gates || [] };
          setFortGateMarks([...(fortChain.current.gates)]);
          paintGhost(cell);
          return;
        }
        const last = chain.points[chain.points.length - 1];
        if (last.x === cell.x && last.z === cell.z) {
          paintGhost(cell);
          return;
        }
        const seg = fortCellsAlong(last.x, last.z, cell.x, cell.z, FORT_LINE_MAX);
        const gates = chain.gates.filter((g) =>
          seg.some((c) => c.x === g.x && c.z === g.z)
        );
        onFortBuildLine(last.x, last.z, cell.x, cell.z, [last, cell], gates);
        const left = chain.gates.filter(
          (g) => !gates.some((u) => u.x === g.x && u.z === g.z)
        );
        fortChain.current = { points: [cell], gates: left };
        setFortGateMarks(left);
        paintGhost(cell);
        return;
      }
      if (drag.current.mode === "box" && boxRef.current) {
        const wpx = Math.abs(e.clientX - drag.current.x0);
        const hpx = Math.abs(e.clientY - drag.current.y0);
        if (wpx < 48 && hpx < 48) {
          boxRef.current.style.display = "none";
          drag.current.mode = "none";
          if (placeArmed(e.clientX, e.clientY)) {
            window.setTimeout(() => {
              skipEntityClickRef.current = false;
              skipFortClickRef.current = false;
            }, 200);
            return;
          }
          {
            const w = panApiRef.current?.clientToWorld(e.clientX, e.clientY);
            if (w) {
              const best = pickEntity(entities, w.x, w.z, true);
              skipEntityClickRef.current = true;
              skipFortClickRef.current = true;
              if (attackMoveRef.current && onAttackMoveRef.current) {
                onAttackMoveRef.current(w.x, w.z);
              } else if (best) {
                const now = performance.now();
                const same =
                  lastPick.current.id === best.id && now - lastPick.current.t < 340;
                lastPick.current = { id: best.id, t: now };
                if (same && onBoxSelect && best.team === myTeam && UNIT_KINDS.has(best.kind)) {
                  const project = panApiRef.current?.worldToClient;
                  const canvas = el.querySelector("canvas");
                  const crect = (canvas || el).getBoundingClientRect();
                  const ids: string[] = [];
                  if (project) {
                    for (const en of entities) {
                      if (en.kind !== best.kind || en.team !== myTeam || en.hp <= 0) continue;
                      const p = project(en.x, en.z);
                      if (!p) continue;
                      if (p.x >= -8 && p.x <= crect.width + 8 && p.y >= -8 && p.y <= crect.height + 8) {
                        ids.push(en.id);
                      }
                    }
                  }
                  if (ids.length) onBoxSelect(ids, false);
                  else onSelect(best.id, e.shiftKey, e.altKey);
                } else {
                  onSelect(best.id, e.shiftKey, e.altKey);
                }
              } else onGroundClick(w.x, w.z);
              window.setTimeout(() => {
                skipEntityClickRef.current = false;
                skipFortClickRef.current = false;
              }, 200);
            }
          }
          return;
        }
        const w = Math.abs(e.clientX - drag.current.x0);
        const h = Math.abs(e.clientY - drag.current.y0);
        boxRef.current.style.display = "none";
        if ((w > 8 || h > 8) && onBoxSelect) {
          const canvas = el.querySelector("canvas");
          const crect = (canvas || el).getBoundingClientRect();
          // Screen-space AABB of the drag (canvas-local pixels)
          const x0 = Math.min(drag.current.x0, e.clientX) - crect.left;
          const y0 = Math.min(drag.current.y0, e.clientY) - crect.top;
          const x1 = Math.max(drag.current.x0, e.clientX) - crect.left;
          const y1 = Math.max(drag.current.y0, e.clientY) - crect.top;
          const project = panApiRef.current?.worldToClient;
          const ids: string[] = [];
          if (project) {
            for (const en of entities) {
              if (en.team !== myTeam || !UNIT_KINDS.has(en.kind) || en.hp <= 0) continue;
              const p = project(en.x, en.z);
              if (!p) continue;
              // Small pad so skinny units near the edge still count
              if (p.x >= x0 - 6 && p.x <= x1 + 6 && p.y >= y0 - 10 && p.y <= y1 + 10) {
                ids.push(en.id);
              }
            }
          }
          if (ids.length) {
            skipEntityClickRef.current = true;
            onBoxSelect(ids, !!e.shiftKey);
            // Clear after R3F's click/pointerup cascade
            window.setTimeout(() => {
              skipEntityClickRef.current = false;
            }, 0);
          }
        }
      }
      drag.current.mode = "none";
    };
    const onClick = (e: MouseEvent) => {
      if (e.button !== 0 || !onMap(e)) return;
      if (!placeBuildModeRef.current) return;
      placeArmed(e.clientX, e.clientY);
    };
    el.addEventListener("pointerdown", onDown, true);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp, true);
    el.addEventListener("click", onClick, true);
    return () => {
      el.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp, true);
      el.removeEventListener("click", onClick, true);
    };
  }, [
    entities,
    interactive,
    myTeam,
    onSelect,
    onBoxSelect,
    onFortBuildLine,
    onGroundClick,
    onGroundRightClick,
    onEntityRightClick,
    panSpeed,
    previewFromDrag,
    projectFortAxis,
  ]);

  const glProps = canvasGlProps(quality, { forceHighPerf: preferHighPerfGpu });
  const dpr = dprForQuality(quality);
  const gatedContext = (id: string) => {
    if (skipEntityClickRef.current) return;
    onEntityRightClick(id);
  };
  const gatedSelect = (id: string, shift: boolean, alt?: boolean) => {
    const clicked = entitiesRef.current.find((en) => en.id === id);
    const clickedUnit = !!clicked && UNIT_KINDS.has(clicked.kind);
    if ((skipEntityClickRef.current || skipFortClickRef.current) && !clickedUnit) return;
    // Alt+click razes own fort even while wall-draw mode is on
    if (alt) {
      onSelect(id, shift, true);
      return;
    }
    if (fortBuildMode) return;
    onSelect(id, shift, false);
  };
  const gatedGround = (x: number, z: number) => {
    if (skipEntityClickRef.current || skipFortClickRef.current) return;
    if (fortBuildMode) return; // fort places on drag pointerup
    onGroundClick(x, z);
  };

  return (
    <div className="empire-stage" ref={wrapRef} style={{ touchAction: "none" }}>
      <div className="empire-canvas-wrap">
        {!webglOk ? (
          <div className="empire-webgl-fail">
            {webglMsg ||
              "WebGL unavailable — enable hardware acceleration so Empire can use your local GPU."}
          </div>
        ) : (
          <Canvas
            shadows={quality !== "low"}
            orthographic
            camera={{
              position: [18, 26, 18],
              zoom: 11,
              near: 2,
              far: 900,
            }}
            dpr={dpr}
            gl={{
              ...glProps,
              logarithmicDepthBuffer: true,
              powerPreference: preferHighPerfGpu
                ? "high-performance"
                : glProps.powerPreference,
            }}
            onCreated={({ gl }) => {
              applyRendererGpuTune(gl, quality, {
                exposure: quality === "high" ? 1.1 : 1.0,
              });
            }}
            onPointerMissed={() => {
              /* keep selection */
            }}
          >
            <Scene
              entities={entities}
              selectedIds={selectedIds}
              onSelect={gatedSelect}
              onGround={gatedGround}
              onGroundContext={onGroundRightClick}
              onEntityContext={gatedContext}
              panSpeed={panSpeed}
              fog={fog}
              water={water}
              beaches={beaches}
              panApiRef={panApiRef}
              dragPan={dragPan}
              orbitNudge={orbitNudge}
              edgePan={edgePan}
              quality={quality}
              myTeam={myTeam}
              fortPreview={fortPreview}
              fortPreviewGate={fortBuildGate}
              fortGateMarks={fortGateMarks}
              mapSeed={seed}
              mapBiome={mapBiome}
              lookAt={lookAt}
            />
          </Canvas>
        )}
      </div>
      <div ref={boxRef} className="empire-box-select" />
      <Minimap
        entities={entities}
        fog={fog}
        myTeam={myTeam}
        onPan={onPanMinimap}
      />
      {children}
      <div className="empire-iso-hint">
        LMB select · double-click same type · RMB order · A then click attack-move · wall: click, click, right-click ends · G marks a gate · WASD / edge pan · MMB orbit · scroll zoom
      </div>
    </div>
  );
}
